/**
 * Figaro — assistant workspace. The landing view is the chat itself; the
 * document library lives one click away at #/figaro/documents.
 * Workscopes (evaluations + MPD library) live under #/figaro/workscopes.
 * Lease return/delivery conditions: #/figaro/lease-returns.
 * Invoice audit: #/figaro/invoice-audit.
 */
import { renderWorkscopesRoute } from "./workscopes-v2.jsx";
import { renderLeaseReturnsRoute } from "./lease-returns-v2.jsx";
import { renderInvoiceAuditRoute } from "./invoice-audit-v2.jsx";
import {
  extractsFromChatCitations,
  renderDocExtractsViewer,
} from "./figaro-doc-extracts-viewer.jsx";

export const FIGARO_NAV = {
  group: "figaro",
  label: "Figaro",
  adminOnly: true,
  defaultOpen: true,
  children: [
    { key: "figaro", label: "Chat", adminOnly: true },
    { key: "figaro/documents", label: "Documents", adminOnly: true },
    { key: "figaro/workscopes", label: "Workscopes", adminOnly: true },
    { key: "figaro/workscopes/mpds", label: "MPD library", adminOnly: true },
    { key: "figaro/lease-returns", label: "Lease return", adminOnly: true },
    { key: "figaro/invoice-audit", label: "Invoice audit", adminOnly: true },
    { key: "figaro/playbooks", label: "Playbooks", adminOnly: true },
  ],
};

export function isFigaroRoute(routeKey) {
  const key = String(routeKey || "").trim();
  return key === "figaro" || key.startsWith("figaro/")
    || key === "perkins" || key.startsWith("perkins/");
}

/** Normalize legacy #/perkins… hashes to #/figaro… */
export function normalizeFigaroRouteKey(routeKey) {
  const key = String(routeKey || "").trim();
  if (key === "perkins") return "figaro";
  if (key.startsWith("perkins/")) return `figaro/${key.slice("perkins/".length)}`;
  return key;
}

const STYLE_ID = "figaro-styles";

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
@keyframes figaro-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes figaro-blink { 0%, 45% { opacity: 1; } 50%, 100% { opacity: 0; } }
@keyframes figaro-orbit { to { transform: rotate(360deg); } }
@keyframes figaro-breathe { 0%, 100% { opacity: .35; transform: scale(.85); } 50% { opacity: 1; transform: scale(1); } }
@keyframes figaro-sheen { 0% { background-position: 120% 0; } 100% { background-position: -20% 0; } }
.figaro-rise { animation: figaro-rise .28s cubic-bezier(.22,.68,.36,1) both; }
.figaro-caret::after { content: "▍"; margin-left: 1px; animation: figaro-blink 1s steps(1) infinite; color: #9ca3af; }
.figaro-spin { width: 13px; height: 13px; border-radius: 9999px; border: 2px solid #e5e7eb; border-top-color: #b91c1c; animation: figaro-orbit .7s linear infinite; }
.figaro-dot { width: 7px; height: 7px; border-radius: 9999px; background: #b91c1c; animation: figaro-breathe 1.4s ease-in-out infinite; }
.figaro-live { background-image: linear-gradient(90deg, #6b7280 30%, #111827 50%, #6b7280 70%); background-size: 220% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: figaro-sheen 1.8s linear infinite; }
.figaro-scroll { scroll-behavior: smooth; }
.figaro-md p { margin: 0 0 .6rem; }
.figaro-md p:last-child { margin-bottom: 0; }
.figaro-md ul { margin: 0 0 .6rem; padding-left: 1.1rem; list-style: disc; }
.figaro-md li { margin: .15rem 0; }
.figaro-md code { background: #f3f4f6; border-radius: .25rem; padding: .05rem .3rem; font-size: .85em; }
.figaro-cite { display: inline-block; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; border-radius: .3rem; padding: 0 .28rem; font-size: .72em; font-weight: 600; vertical-align: baseline; }
/* Flowbite's form base draws a dark border on bare textareas — the composer supplies its own frame. */
.figaro-input, .figaro-input:focus, .figaro-input:focus-visible {
  border: 0 !important; outline: 0 !important; box-shadow: none !important;
  --tw-ring-shadow: 0 0 #0000 !important; --tw-ring-offset-shadow: 0 0 #0000 !important;
  background-color: transparent !important;
}
.figaro-block { background: #fff; border: 1px solid #e5e7eb; border-radius: .75rem; box-shadow: 0 1px 2px rgba(16,24,40,.05); margin: .7rem 0; overflow: hidden; }
.figaro-block-head { display: flex; align-items: center; justify-content: space-between; gap: .75rem; padding: .4rem .5rem .4rem .75rem; border-bottom: 1px solid #f3f4f6; background: #fcfcfd; }
.figaro-block-title { font-size: .66rem; letter-spacing: .06em; text-transform: uppercase; color: #9ca3af; font-weight: 600; }
.figaro-copy { font-size: .72rem; font-weight: 600; color: #374151; background: #fff; border: 1px solid #e5e7eb; border-radius: .4rem; padding: .2rem .55rem; cursor: pointer; white-space: nowrap; }
.figaro-copy:hover { border-color: #d1d5db; background: #f9fafb; }
.figaro-copy[data-state="done"] { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
.figaro-draft { padding: .8rem .9rem; white-space: pre-wrap; color: #111827; font-size: .875rem; line-height: 1.6; }
.figaro-code { padding: .8rem .9rem; white-space: pre; overflow-x: auto; color: #111827; font-size: .8rem; line-height: 1.5; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
.figaro-tablewrap { overflow-x: auto; }
.figaro-table { min-width: 100%; border-collapse: collapse; font-size: .8125rem; }
.figaro-table th { text-align: left; color: #fff; font-weight: 600; padding: .5rem .75rem; border-bottom: 1px solid rgba(0,0,0,.12); white-space: nowrap; background: #b91c1c; font-size: .7rem; letter-spacing: .04em; text-transform: uppercase; }
.figaro-table td { padding: .5rem .75rem; border-bottom: 1px solid #f3f4f6; color: #111827; vertical-align: top; }
.figaro-table tbody tr:last-child td { border-bottom: 0; }
`;
  document.head.appendChild(style);
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Inline markdown on already-escaped text. */
function inlineHtml(value) {
  return String(value)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\[(S\d+)\]/g, '<span class="figaro-cite">$1</span>');
}

/** Escape then apply inline markdown — for values coming straight from the model. */
function renderInline(value) {
  return inlineHtml(escapeHtml(value));
}

/** Small, deliberately limited markdown subset — everything is escaped first. */
function renderMarkdown(text) {
  const lines = escapeHtml(text).split("\n");
  const out = [];
  let list = null;
  const inline = inlineHtml;

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    if (bullet) {
      list = list || [];
      list.push(`<li>${inline(bullet[1])}</li>`);
      continue;
    }
    if (list) {
      out.push(`<ul>${list.join("")}</ul>`);
      list = null;
    }
    if (line.trim()) out.push(`<p>${inline(line)}</p>`);
  }
  if (list) out.push(`<ul>${list.join("")}</ul>`);
  return out.join("") || "<p></p>";
}

/* ---------- copy-ready blocks (drafts + tables) ---------- */

/** Fence languages that hold prose meant to be pasted somewhere, not code. */
const DRAFT_LANGS = new Set(["", "draft", "email", "mail", "message", "letter", "note", "text", "txt"]);

/** Machine-only fences — never show raw JSON to the user (server usually strips these). */
const HIDDEN_FENCE_LANGS = new Set(["eol_trueup", "eol-trueup", "eoltrueup"]);

const DRAFT_TITLES = {
  email: "Email draft — ready to copy",
  mail: "Email draft — ready to copy",
  letter: "Letter draft — ready to copy",
  message: "Message draft — ready to copy",
  note: "Note — ready to copy",
};

function isTableRow(line) {
  const trimmed = String(line || "").trim();
  return trimmed.startsWith("|") && trimmed.indexOf("|", 1) > 0;
}

function isTableDivider(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed.startsWith("|") || !trimmed.includes("-")) return false;
  return splitTableRow(trimmed).every((cell) => /^:?-{1,}:?$/.test(cell.trim()));
}

function splitTableRow(line) {
  const trimmed = String(line || "").trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((cell) => cell.trim());
}

/** Split an answer into markdown, fenced blocks and tables, tolerating a fence still streaming. */
function splitBlocks(text) {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let buffer = [];
  const flush = () => {
    if (buffer.some((line) => line.trim())) blocks.push({ kind: "md", lines: buffer });
    buffer = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const fence = lines[i].match(/^\s*```+\s*([\w-]*)\s*$/);
    if (fence) {
      flush();
      const body = [];
      let closed = false;
      for (i += 1; i < lines.length; i += 1) {
        if (/^\s*```+\s*$/.test(lines[i])) {
          closed = true;
          break;
        }
        body.push(lines[i]);
      }
      blocks.push({ kind: "fence", lang: (fence[1] || "").toLowerCase(), lines: body, open: !closed });
      continue;
    }
    if (isTableRow(lines[i]) && isTableDivider(lines[i + 1])) {
      flush();
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(lines[i]);
        i += 1;
      }
      i -= 1;
      blocks.push({ kind: "table", lines: rows });
      continue;
    }
    buffer.push(lines[i]);
  }
  flush();
  return blocks;
}

function legacyCopy({ text, html }) {
  const holder = document.createElement("div");
  if (html) holder.innerHTML = html;
  else holder.textContent = text;
  holder.setAttribute("contenteditable", "true");
  holder.style.cssText = "position:fixed;left:-9999px;top:0;white-space:pre-wrap;";
  document.body.appendChild(holder);
  const range = document.createRange();
  range.selectNodeContents(holder);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  selection.removeAllRanges();
  holder.remove();
  return ok;
}

/** Write both flavours when we have them so Outlook/Word keep the table styling. */
async function writeClipboard({ text, html }) {
  try {
    if (html && window.ClipboardItem && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return true;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the selection-based path */
  }
  return legacyCopy({ text, html });
}

function copyButton(label, getPayload) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "figaro-copy";
  button.textContent = label;
  button.onclick = async () => {
    const ok = await writeClipboard(getPayload());
    button.dataset.state = ok ? "done" : "";
    button.textContent = ok ? "Copied" : "Copy failed";
    setTimeout(() => {
      button.dataset.state = "";
      button.textContent = label;
    }, 1500);
  };
  return button;
}

function blockCard(title, actions, content) {
  const card = document.createElement("div");
  card.className = "figaro-block";
  const head = document.createElement("div");
  head.className = "figaro-block-head";
  const label = document.createElement("span");
  label.className = "figaro-block-title";
  label.textContent = title;
  head.append(label, ...actions);
  card.append(head, content);
  return card;
}

function fenceNode(block) {
  if (HIDDEN_FENCE_LANGS.has(block.lang)) return null;
  const raw = block.lines.join("\n").replace(/\s+$/, "");
  const isDraft = DRAFT_LANGS.has(block.lang);
  const content = document.createElement("div");
  content.className = isDraft ? "figaro-draft" : "figaro-code";
  content.textContent = raw;
  const title = isDraft
    ? DRAFT_TITLES[block.lang] || "Draft — ready to copy"
    : `${block.lang.toUpperCase()} — ready to copy`;
  const actions = block.open ? [] : [copyButton("Copy", () => ({ text: raw }))];
  return blockCard(title, actions, content);
}

/** Inline-styled copy of .figaro-table so the styling survives a paste into mail or Word. */
function tableClipboardHtml(headers, rows) {
  const cell = (value, header) =>
    `<${header ? "th" : "td"} style="${
      header
        ? "text-align:left;color:#6b7280;font-weight:600;padding:6px 12px;border-bottom:1px solid #e5e7eb;"
        : "padding:6px 12px;border-bottom:1px solid #f3f4f6;color:#111827;vertical-align:top;"
    }">${escapeHtml(value)}</${header ? "th" : "td"}>`;
  const head = headers.length
    ? `<thead><tr>${headers.map((value) => cell(value, true)).join("")}</tr></thead>`
    : "";
  const body = `<tbody>${rows
    .map((row) => `<tr>${row.map((value) => cell(value, false)).join("")}</tr>`)
    .join("")}</tbody>`;
  return `<table style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#111827;">${head}${body}</table>`;
}

function tableNode(block) {
  const [headerLine, , ...rest] = block.lines;
  const headers = splitTableRow(headerLine);
  const rows = rest.map((line) => splitTableRow(line));
  const width = Math.max(headers.length, ...rows.map((row) => row.length), 1);
  const pad = (row) => Array.from({ length: width }, (_, i) => row[i] || "");
  const paddedHeaders = pad(headers);
  const paddedRows = rows.map(pad);

  const table = document.createElement("table");
  table.className = "figaro-table";
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const value of paddedHeaders) {
    const th = document.createElement("th");
    th.innerHTML = renderInline(value);
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  const tbody = document.createElement("tbody");
  for (const row of paddedRows) {
    const tr = document.createElement("tr");
    for (const value of row) {
      const td = document.createElement("td");
      td.innerHTML = renderInline(value);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.append(thead, tbody);

  const wrap = document.createElement("div");
  wrap.className = "figaro-tablewrap";
  wrap.appendChild(table);

  const plain = [paddedHeaders, ...paddedRows].map((row) => row.join("\t")).join("\n");
  const button = copyButton("Copy table", () => ({
    text: plain,
    html: tableClipboardHtml(paddedHeaders, paddedRows),
  }));
  return blockCard("Table — ready to copy", [button], wrap);
}

/** Render an answer into `host`, turning fences and tables into copyable cards. */
function paintMarkdown(host, text) {
  const blocks = splitBlocks(text);
  host.replaceChildren();
  if (!blocks.length) {
    const empty = document.createElement("div");
    empty.innerHTML = renderMarkdown(text);
    host.appendChild(empty);
    return;
  }
  for (const block of blocks) {
    if (block.kind === "fence") {
      const node = fenceNode(block);
      if (node) host.appendChild(node);
      continue;
    }
    if (block.kind === "table") {
      host.appendChild(tableNode(block));
      continue;
    }
    const segment = document.createElement("div");
    segment.innerHTML = renderMarkdown(block.lines.join("\n"));
    host.appendChild(segment);
  }
}

function highlightHtml(text, query) {
  const esc = escapeHtml(text);
  const q = String(query || "").trim();
  if (!q || q.length < 2) return esc;
  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  try {
    return esc.replace(new RegExp(`(${safe})`, "gi"), '<mark class="bg-yellow-200 px-0.5 rounded">$1</mark>');
  } catch {
    return esc;
  }
}

function panel(el, title, bodyNodes) {
  return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3" }, [
    title ? el("h2", { class: "text-lg font-semibold text-gray-900", text: title }) : null,
    ...bodyNodes,
  ]);
}

function citationLink(el, citation, apiUrl) {
  const isWeb = citation?.kind === "web" || Boolean(citation?.url);
  if (isWeb) {
    return el("a", {
      href: citation.url,
      target: "_blank",
      rel: "noopener noreferrer",
      class:
        "px-2 py-0.5 text-xs rounded-full border border-sky-200 bg-sky-50 text-sky-800 hover:border-sky-300",
      text: `${citation.ref} · ${citation.title || citation.url}`,
      title: citation.url || "",
    });
  }
  if (citation?.kind === "workbook" && citation.documentId) {
    const href = typeof apiUrl === "function"
      ? apiUrl(`/api/ai/documents/${citation.documentId}/file`)
      : `/api/ai/documents/${citation.documentId}/file`;
    return el("a", {
      href,
      target: "_blank",
      rel: "noopener noreferrer",
      class:
        "px-2 py-0.5 text-xs rounded-full border border-emerald-200 bg-emerald-50 text-emerald-900 hover:border-emerald-300",
      text: `${citation.ref || "WB"} · Download Excel`,
      title: citation.title || citation.filename || "EOL true-up workbook",
    });
  }
  return el("a", {
    href: `#/figaro/documents/${citation.documentId}?page=${citation.pageStart || 1}`,
    class: "px-2 py-0.5 text-xs rounded-full border border-gray-200 bg-white text-gray-600 hover:border-gray-300",
    text: `${citation.ref} · ${citation.title}`,
  });
}

function workbookCard(el, apiUrl, attachment) {
  const title = attachment.title || attachment.filename || "EOL true-up workbook";
  const gapCount = Array.isArray(attachment.gaps) ? attachment.gaps.length : 0;
  return el("div", { class: "figaro-block" }, [
    el("div", { class: "figaro-block-head" }, [
      el("span", { class: "figaro-block-title", text: "Excel workbook — house true-up template" }),
      el("a", {
        href: apiUrl(`/api/ai/documents/${attachment.documentId}/file`),
        class: "figaro-copy",
        text: "Download .xlsx",
        target: "_blank",
        rel: "noopener noreferrer",
        download: attachment.filename || "",
      }),
    ]),
    el("div", { class: "px-3 py-2 text-sm text-gray-700 space-y-1" }, [
      el("div", { text: title }),
      el("div", {
        class: "text-xs text-gray-500",
        text:
          gapCount > 0
            ? `${gapCount} gap${gapCount === 1 ? "" : "s"} flagged in the Gaps tab — fill blue inputs from source docs before circulating.`
            : "Blue = inputs · Black = formulas · Green = cross-sheet. Open in Excel or Google Sheets.",
      }),
    ]),
  ]);
}

function statusBadge(el, status) {
  const s = String(status || "");
  const tone =
    s === "INDEXED"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : s === "FAILED"
        ? "bg-red-50 text-red-800 border-red-200"
        : s === "PARSING" || s === "UPLOADED"
          ? "bg-amber-50 text-amber-900 border-amber-200"
          : "bg-gray-50 text-gray-700 border-gray-200";
  return el("span", { class: `inline-flex px-2 py-0.5 text-xs font-medium border rounded ${tone}`, text: s || "—" });
}

function ingestProgressBar(el, doc) {
  const st = String(doc?.status || "").toUpperCase();
  if (st !== "PARSING" && st !== "UPLOADED") return null;
  const total = Number(doc.pageCount || 0);
  const q = doc.ocrQuality != null ? Number(doc.ocrQuality) : null;
  const pct = total > 0 && q != null && Number.isFinite(q) && q >= 0 ? Math.min(1, Math.max(0, q)) : null;
  const current = pct != null ? Math.round(pct * total) : 0;
  const shown = pct == null ? null : Math.round(pct * 100);
  const remain = current >= 2 && total > current ? Math.round(((total - current) * 5) / 60) : null;
  const label =
    total > 0 && current > 0
      ? `Page ${current} of ${total}${remain != null ? ` · about ${Math.max(1, remain)} min left` : ""}`
      : total > 0
        ? `Preparing ${total} pages…`
        : "Parsing scanned pages — this can take up to 20 minutes";
  const width = shown == null ? null : Math.max(3, shown);
  return el("div", { class: "w-full max-w-sm space-y-1 mt-1" }, [
    el("div", {
      class: "text-lg font-bold tabular-nums text-emerald-700 leading-none",
      text: shown != null ? `${shown}%` : "…",
    }),
    el("div", { class: "h-2 bg-gray-100 rounded-full overflow-hidden" }, [
      el("div", {
        class: width == null ? "h-full w-1/3 bg-emerald-500 animate-pulse rounded-full" : "h-full bg-emerald-600 rounded-full",
        style: width == null ? "" : `width:${width}%`,
      }),
    ]),
    el("div", { class: "text-xs text-gray-600 tabular-nums", text: label }),
  ]);
}

function ocrBadge(el, ocrStatus, emptyPages, pageCount) {
  const s = String(ocrStatus || "—");
  const empty = Number(emptyPages) || 0;
  const pages = Number(pageCount) || 0;
  const mostlyEmpty = pages > 0 && empty / pages > 0.45;
  const label = mostlyEmpty
    ? `NEEDS OCR · ${empty}/${pages} empty`
    : s === "OCR_WEAK" && empty
      ? `OCR_WEAK · ${empty} empty`
      : s === "OCR_VERIFIED"
        ? "VERIFIED vs scan"
        : s;
  const bad = mostlyEmpty || s === "OCR_EMPTY" || s === "NEEDS_OCR";
  const tone = bad
    ? "bg-red-50 text-red-800 border-red-200"
    : s === "NATIVE_TEXT" || s === "OCR_OK" || s === "VISION_EXTRACTED" || s === "OCR_VERIFIED"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : "bg-amber-50 text-amber-900 border-amber-200";
  return el("span", {
    class: `inline-flex px-2 py-0.5 text-xs font-medium border rounded ${tone}`,
    text: label,
    title: bad
      ? "Most pages have little or no extractable text (likely a scanned PDF). Chat can only use what was extracted — re-upload a text-layer PDF or run Re-index."
      : s === "OCR_VERIFIED"
        ? "An agent compared extracted text to the page image and judged it faithful."
        : s === "OCR_WEAK"
          ? "Extraction is weak or unfaithful — open the document and Re-index, or upload a cleaner PDF."
          : "Text layer used for search and chat.",
  });
}

/* ─── chat ─────────────────────────────────────────────────────────── */

function contextLabel(context) {
  if (!context || context.scopeLevel === "GLOBAL") return "Everything";
  return context.label;
}

function formatSpend(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

const DEFAULT_TASK_TYPES = [
  { id: "generic", label: "Generic" },
  { id: "legal", label: "Legal / contracts" },
  { id: "eol", label: "EOL true-up" },
  { id: "workscope", label: "Workscope evaluation" },
  { id: "email", label: "Email draft" },
  { id: "invoice", label: "Invoice audit" },
];

async function streamChat(apiUrl, body, handlers) {
  const response = await fetch(apiUrl("/api/ai/chat/stream"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
  });
  if (response.status === 401) {
    window.location.href = "/login.html";
    throw new Error("Session expired");
  }
  if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let split;
    while ((split = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      let event = "message";
      const data = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trim());
      }
      if (!data.length) continue;
      let payload;
      try {
        payload = JSON.parse(data.join("\n"));
      } catch {
        continue;
      }
      if (event === "step") handlers.onStep(payload);
      else if (event === "delta") handlers.onDelta(payload.text || "");
      else if (event === "done") {
        finished = true;
        handlers.onDone(payload);
      } else if (event === "failed") {
        finished = true;
        handlers.onFail(payload);
      }
    }
  }
  if (!finished) throw new Error("Connection closed early");
}

function renderChat({ el, fetchJson, apiUrl, cleanText, host }) {
  ensureStyles();

  let workspace = { global: { documents: 0, indexed: 0, runs: 0, tools: [] }, operators: [], assets: [] };
  let context = { scopeLevel: "GLOBAL", label: "Everything", operatorId: null, assetId: null };
  let sessionId = null;
  let sessionSpend = 0;
  let lastTurnSpend = 0;
  let busy = false;
  let contextDocs = [];
  let uploads = [];
  let taskType = "generic";
  let taskTypes = DEFAULT_TASK_TYPES.slice();

  const thread = el("div", { class: "figaro-scroll flex-1 overflow-y-auto px-1 space-y-5" });
  const spendEl = el("span", {
    class: "shrink-0 self-end pb-2 text-[11px] tabular-nums text-gray-400",
    title: "This chat · last turn / session total",
    text: "$0",
  });
  const personaFlag = el("span", {
    class:
      "hidden shrink-0 self-end pb-2 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border border-sky-200 bg-sky-50 text-sky-800",
    title: "Analyst on this turn",
    text: "",
  });
  const taskSelect = el(
    "select",
    {
      class:
        "shrink-0 self-end mb-1.5 max-w-[11rem] rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-800",
      title: "Task type — picks the playbook and analyst",
      onchange: () => {
        taskType = taskSelect.value || "generic";
      },
    },
    taskTypes.map((t) => el("option", { value: t.id, text: t.label, selected: t.id === taskType }))
  );
  const contextButton = el("button", {
    type: "button",
    class:
      "inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:border-gray-300 transition",
  });
  const contextMenu = el("div", {
    class:
      "hidden absolute z-30 mt-2 w-96 max-h-96 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg p-1.5",
  });
  const recentButton = el("button", {
    type: "button",
    class: "px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:border-gray-300 transition",
    text: "Recent",
  });
  const filesButton = el("button", {
    type: "button",
    class: "px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:border-gray-300 transition",
    text: "Files",
    title: "Files Figaro can read in this context",
  });
  const filesPanel = el("div", {
    class: "hidden mb-2 rounded-xl border border-gray-200 bg-white p-2 max-h-60 overflow-y-auto",
  });
  const filePicker = el("input", { type: "file", multiple: true, class: "hidden" });
  const dropHint = el("div", {
    class: "px-4 py-2 rounded-xl bg-white border border-dae text-sm font-medium text-gray-800 shadow-sm",
    text: "Drop files here",
  });
  const dropOverlay = el(
    "div",
    {
      class:
        "hidden absolute inset-0 z-20 rounded-xl border-2 border-dashed border-dae bg-red-50/70 items-center justify-center pointer-events-none",
    },
    [dropHint]
  );
  const recentList = el("div", { class: "flex-1 overflow-y-auto pr-1" });
  const recentRail = el("aside", { class: "hidden shrink-0 w-64 flex-col border-r border-gray-200 pr-2 mr-1" }, [
    el("div", { class: "flex items-center justify-between gap-2 pb-2" }, [
      el("span", {
        class: "text-[11px] font-semibold uppercase tracking-wide text-gray-400",
        text: "Recent chats",
      }),
      el("button", {
        type: "button",
        class: "px-1.5 text-gray-400 hover:text-gray-700 leading-none",
        text: "×",
        title: "Collapse",
        onclick: () => setRecentRail(false),
      }),
    ]),
    recentList,
  ]);

  function setRecentRail(open) {
    recentRail.classList.toggle("hidden", !open);
    recentRail.classList.toggle("flex", open);
    recentButton.classList.toggle("border-gray-300", open);
    recentButton.classList.toggle("bg-gray-50", open);
    if (open) void refreshRecent();
  }

  /**
   * Jump to the newest message. The thread scrolls smoothly while streaming, so an
   * animated jump would crawl through a long history; force an instant one and repeat
   * it across two frames because the rise animation settles heights after paint.
   */
  function jumpThreadToBottom() {
    const jump = () => {
      thread.style.scrollBehavior = "auto";
      thread.scrollTop = thread.scrollHeight;
      thread.style.scrollBehavior = "";
    };
    jump();
    requestAnimationFrame(() => {
      jump();
      requestAnimationFrame(jump);
    });
  }

  function paintSpend(sessionValue, turnValue = null) {
    sessionSpend = Number(sessionValue) || 0;
    if (turnValue != null) lastTurnSpend = Number(turnValue) || 0;
    else if (sessionSpend <= 0) lastTurnSpend = 0;
    spendEl.textContent =
      lastTurnSpend > 0
        ? `${formatSpend(lastTurnSpend)} / ${formatSpend(sessionSpend)}`
        : formatSpend(sessionSpend);
  }

  function paintPersona(persona) {
    const name = persona?.callsign || "";
    if (!name) {
      personaFlag.classList.add("hidden");
      personaFlag.textContent = "";
      return;
    }
    personaFlag.classList.remove("hidden");
    personaFlag.textContent = name;
    personaFlag.title = persona?.blurb || `${name} is handling this turn`;
  }

  function paintTaskOptions(list) {
    taskTypes = list?.length ? list : DEFAULT_TASK_TYPES.slice();
    if (!taskTypes.some((t) => t.id === taskType)) taskType = "generic";
    taskSelect.replaceChildren(
      ...taskTypes.map((t) =>
        el("option", { value: t.id, text: t.label, selected: t.id === taskType })
      )
    );
  }

  /** Best guess so a dropped file lands under a sensible type without asking. */
  function docTypeForFile(name) {
    const lower = String(name || "").toLowerCase();
    if (lower.endsWith(".eml") || lower.endsWith(".msg")) return "EMAIL";
    if (/\bcrs\b|certificate.?of.?release.?to.?service/.test(lower)) return "CRS";
    if (/invoice|billing|amount.?due/.test(lower)) return "INVOICE";
    if (/claim|insurance.?loss|proof.?of.?loss/.test(lower)) return "CLAIM";
    if (/amend|supplement/.test(lower) && /lease/.test(lower)) return "LEASE_AMENDMENT";
    if (/lease/.test(lower)) return "LEASE";
    if (/legal|agreement|contract|settlement|release|nda/.test(lower)) return "OTHER_LEGAL";
    if (/\bmpd\b|maintenance planning/.test(lower)) return "MPD";
    if (/workscope/.test(lower)) return "WORKSCOPE";
    if (/utilis|utiliz|hours.*cycles/.test(lower)) return "UTILIZATION";
    if (/\.(png|jpe?g|webp)$/.test(lower)) return "SCREENSHOT";
    return "OTHER";
  }

  function fileRow(doc, inherited) {
    const scopeNote = inherited
      ? doc.scopeLevel === "GLOBAL"
        ? "Global"
        : `Operator · ${doc.operatorName || ""}`.trim()
      : "";
    return el("div", { class: "flex items-start gap-2 px-1.5 py-1.5 rounded-lg hover:bg-gray-50" }, [
      el("div", { class: "min-w-0 flex-1" }, [
        el("a", {
          href: `#/figaro/documents/${doc.id}`,
          class: "block text-sm text-gray-900 hover:text-dae hover:underline truncate",
          text: doc.title || doc.originalFilename || `Document ${doc.id}`,
          title: doc.originalFilename || "",
        }),
        el("div", {
          class: "text-[11px] text-gray-400",
          text: [doc.docType, doc.pageCount ? `${doc.pageCount} pages` : null, scopeNote]
            .filter(Boolean)
            .join(" · "),
        }),
      ]),
      statusBadge(el, doc.status),
    ]);
  }

  function paintFiles() {
    const mine = contextDocs.filter((doc) => {
      if (context.scopeLevel === "ASSET") return Number(doc.assetId) === Number(context.assetId);
      if (context.scopeLevel === "OPERATOR") {
        return doc.scopeLevel === "OPERATOR" && Number(doc.operatorId) === Number(context.operatorId);
      }
      return true;
    });
    const inherited = contextDocs.filter((doc) => !mine.includes(doc));
    filesButton.textContent = mine.length ? `Files ${mine.length}` : "Files";

    const nodes = [];
    for (const item of uploads) {
      nodes.push(
        el("div", { class: "flex items-center gap-2 px-1.5 py-1.5" }, [
          el("div", { class: "min-w-0 flex-1 text-sm text-gray-600 truncate", text: item.name }),
          el("span", {
            class: `text-[11px] ${item.state === "failed" ? "text-red-700" : "text-gray-400"}`,
            text: item.state === "failed" ? item.error || "Upload failed" : "Uploading…",
          }),
        ])
      );
    }
    if (mine.length) {
      nodes.push(
        el("div", {
          class: "px-1.5 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500",
          text: `In ${contextLabel(context)}`,
        }),
        ...mine.map((doc) => fileRow(doc, false))
      );
    }
    if (inherited.length) {
      nodes.push(
        el("div", {
          class: "px-1.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400",
          text: "Also readable here",
        }),
        ...inherited.map((doc) => fileRow(doc, true))
      );
    }
    if (!nodes.length) {
      nodes.push(
        el("p", {
          class: "px-1.5 py-2 text-sm text-gray-500",
          text:
            context.scopeLevel === "GLOBAL"
              ? "Nothing from the global library is sent to chat. Pick an operator or asset, or drop a file here."
              : "No files in this context yet. Use + in the composer or drop files on the chat.",
        })
      );
    }
    nodes.push(
      el("div", { class: "pt-2 mt-1 border-t border-gray-100 flex items-center gap-2" }, [
        el("button", {
          type: "button",
          class: "px-2 py-1 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50",
          text: "Add files",
          onclick: () => filePicker.click(),
        }),
        el("a", {
          href: "#/figaro/documents",
          class: "text-xs text-gray-500 hover:text-dae hover:underline",
          text: "Open library",
        }),
      ])
    );
    filesPanel.replaceChildren(el("div", {}, nodes));
  }

  async function refreshContextFiles() {
    try {
      const query = new URLSearchParams();
      query.set("forChat", "1");
      if (context.scopeLevel === "ASSET" && context.assetId) {
        query.set("contextAssetId", String(context.assetId));
      } else if (context.scopeLevel === "OPERATOR" && context.operatorId) {
        query.set("contextOperatorId", String(context.operatorId));
      }
      const data = await fetchJson(apiUrl(`/api/ai/documents?${query.toString()}`));
      contextDocs = data.documents || [];
      const done = new Set(contextDocs.map((doc) => doc.originalFilename));
      uploads = uploads.filter((item) => item.state === "failed" && !done.has(item.name));
      paintFiles();
      // Ingest runs in a worker, so keep checking while anything is still being read.
      const working = contextDocs.some((doc) => doc.status === "UPLOADED" || doc.status === "PARSING");
      if (working && document.body.contains(thread)) setTimeout(() => void refreshContextFiles(), 2500);
    } catch {
      /* the panel is optional */
    }
  }

  async function uploadToContext(files) {
    const list = [...(files || [])].filter(Boolean);
    if (!list.length) return;
    filesPanel.classList.remove("hidden");
    for (const file of list) {
      const item = { name: file.name, state: "uploading" };
      uploads.push(item);
      paintFiles();
      try {
        await uploadDocument({ apiUrl, fetchJson }, file, {
          docType: docTypeForFile(file.name),
          title: file.name,
          scopeLevel: context.scopeLevel,
          operatorId:
            context.scopeLevel === "OPERATOR" || context.scopeLevel === "ASSET"
              ? context.operatorId
              : "",
          assetId: context.scopeLevel === "ASSET" ? context.assetId : "",
        });
        item.state = "indexing";
      } catch (error) {
        item.state = "failed";
        item.error = cleanText(error?.message) || "Upload failed";
      }
      paintFiles();
    }
    await refreshContextFiles();
  }

  function paintContextButton() {
    contextButton.replaceChildren(
      el("span", { class: "font-medium text-gray-800", text: contextLabel(context) }),
      el("span", { class: "text-gray-400", text: "▾" })
    );
  }

  function contextRow(option, { indent = false } = {}) {
    const active =
      option.scopeLevel === context.scopeLevel &&
      (option.operatorId || null) === context.operatorId &&
      (option.assetId || null) === context.assetId;
    const meta = [];
    if (option.documents) meta.push(`${option.documents} document${option.documents === 1 ? "" : "s"}`);
    if (option.runs) meta.push(`${option.runs} in progress`);
    return el(
      "button",
      {
        type: "button",
        class: `w-full text-left rounded-lg hover:bg-gray-50 transition ${indent ? "px-3 py-1.5 pl-5" : "px-3 py-2"} ${active ? "bg-red-50" : ""}`,
        onclick: () => {
          context = {
            scopeLevel: option.scopeLevel,
            label: option.label,
            operatorId: option.operatorId || null,
            assetId: option.assetId || null,
          };
          sessionId = null;
          paintSpend(0);
          contextMenu.classList.add("hidden");
          paintContextButton();
          paintEmptyState();
          void refreshContextFiles();
        },
      },
      [
        el("div", {
          class: indent ? "text-[13px] text-gray-800" : "text-sm text-gray-900",
          text: option.label,
        }),
        meta.length
          ? el("div", { class: "text-xs text-gray-500", text: meta.join(" · ") })
          : null,
      ]
    );
  }

  function contextOptions() {
    const options = [
      {
        scopeLevel: "GLOBAL",
        label: "Everything",
        documents: workspace.global?.documents || 0,
        runs: workspace.global?.runs || 0,
      },
    ];
    for (const operator of workspace.operators || []) {
      options.push({
        scopeLevel: "OPERATOR",
        operatorId: operator.id,
        label: operator.name,
        documents: operator.documents,
        runs: operator.runs,
      });
    }
    for (const asset of workspace.assets || []) {
      const msn = asset.serialNumber || asset.label || `Asset ${asset.id}`;
      const label = asset.operatorName
        ? `${asset.operatorName} · ${asset.label || msn}`
        : asset.label || msn;
      options.push({
        scopeLevel: "ASSET",
        operatorId: asset.operatorId || null,
        assetId: asset.id,
        label,
        documents: asset.documents,
        runs: asset.runs,
        operatorName: asset.operatorName || null,
      });
    }
    return options;
  }

  function paintContextMenu() {
    const options = contextOptions();
    const globalItems = options.filter((o) => o.scopeLevel === "GLOBAL");
    const operators = options.filter((o) => o.scopeLevel === "OPERATOR");
    const assets = options.filter((o) => o.scopeLevel === "ASSET");
    const assetsByOp = new Map();
    const orphanAssets = [];
    for (const asset of assets) {
      if (asset.operatorId) {
        if (!assetsByOp.has(asset.operatorId)) assetsByOp.set(asset.operatorId, []);
        assetsByOp.get(asset.operatorId).push(asset);
      } else {
        orphanAssets.push(asset);
      }
    }

    const nodes = [];
    if (globalItems.length) {
      nodes.push(
        el("div", {
          class: "px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400",
          text: "Everywhere",
        }),
        ...globalItems.map((o) => contextRow(o))
      );
    }

    const opIdsShown = new Set();
    if (operators.length || assets.length) {
      nodes.push(
        el("div", {
          class: "px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400",
          text: "Operators & MSNs",
        })
      );
      for (const op of operators) {
        opIdsShown.add(op.operatorId);
        nodes.push(contextRow(op));
        for (const asset of assetsByOp.get(op.operatorId) || []) {
          nodes.push(
            contextRow(
              {
                ...asset,
                // Nest under operator: show MSN line without repeating the operator name
                label: asset.label.includes(" · ")
                  ? asset.label.split(" · ").slice(1).join(" · ") || asset.label
                  : asset.label,
              },
              { indent: true }
            )
          );
        }
      }
      // Assets whose operator has no operator-level row (counts rolled only on assets)
      for (const [opId, list] of assetsByOp.entries()) {
        if (opIdsShown.has(opId)) continue;
        const name = list[0]?.operatorName || `Operator ${opId}`;
        nodes.push(
          el("div", {
            class: "px-3 pt-1.5 pb-0.5 text-[11px] font-medium text-gray-500",
            text: name,
          })
        );
        for (const asset of list) {
          nodes.push(
            contextRow(
              {
                ...asset,
                label: asset.label.includes(" · ")
                  ? asset.label.split(" · ").slice(1).join(" · ") || asset.label
                  : asset.label,
              },
              { indent: true }
            )
          );
        }
      }
      for (const asset of orphanAssets) {
        nodes.push(contextRow(asset, { indent: true }));
      }
    }

    if (!nodes.length) {
      nodes.push(
        el("p", {
          class: "px-3 py-2 text-sm text-gray-500",
          text: "No document contexts yet. Upload files to an operator or MSN in Documents.",
        })
      );
    }

    contextMenu.replaceChildren(...nodes);
  }

  /** A new chat opens on a blank thread — no greeting, no context chips. */
  function paintEmptyState() {
    if (thread.dataset.started === "1") return;
    thread.replaceChildren();
  }

  function bubbleUser(text) {
    return el("div", { class: "figaro-rise flex justify-end" }, [
      el("div", {
        class:
          "max-w-[85%] rounded-2xl rounded-br-md text-white px-4 py-2.5 text-sm whitespace-pre-wrap",
        style: "background-color: rgb(192, 0, 0);",
        text,
      }),
    ]);
  }

  function startAssistantBubble() {
    const steps = el("div", { class: "space-y-1.5" });
    const timer = el("span", { class: "text-xs text-gray-400 tabular-nums", text: "0.0s" });
    const liveLabel = el("span", { class: "figaro-live text-sm font-medium", text: "Thinking" });
    const body = el("div", { class: "figaro-md text-sm text-gray-800 leading-relaxed" });
    const sources = el("div", { class: "pt-1" });
    const meta = el("div", { class: "flex flex-wrap items-center gap-2 pt-0.5" });
    const wrap = el("div", { class: "figaro-rise max-w-[92%] space-y-2" }, [
      el("div", { class: "flex items-center gap-2" }, [
        el("div", { class: "figaro-spin" }),
        liveLabel,
        timer,
      ]),
      steps,
      body,
      meta,
      sources,
    ]);
    thread.appendChild(wrap);

    const startedAt = Date.now();
    const tick = setInterval(() => {
      timer.textContent = `${((Date.now() - startedAt) / 1000).toFixed(1)}s`;
    }, 100);
    const rows = new Map();
    let text = "";

    const scroll = () => {
      thread.scrollTop = thread.scrollHeight;
    };

    return {
      step(step) {
        if (step?.persona?.callsign) {
          paintPersona(step.persona);
          liveLabel.textContent = `${step.persona.callsign} thinking`;
        }
        for (const [, row] of rows) {
          row.mark.replaceChildren(el("span", { class: "text-emerald-600 text-xs", text: "✓" }));
          row.label.className = "text-xs text-gray-400";
        }
        const mark = el("span", { class: "flex items-center justify-center w-3", text: "" }, []);
        mark.appendChild(el("span", { class: "figaro-dot" }));
        const label = el("span", { class: "text-xs text-gray-700" , text: step.label });
        const row = el("div", { class: "figaro-rise flex items-center gap-2" }, [mark, label]);
        rows.set(step.key, { mark, label, row });
        steps.appendChild(row);
        scroll();
      },
      delta(chunk) {
        text += chunk;
        body.className = "figaro-md text-sm text-gray-800 leading-relaxed figaro-caret";
        paintMarkdown(body, text);
        scroll();
      },
      finish(payload) {
        clearInterval(tick);
        const elapsed = ((Date.now() - startedAt) / 1000).toFixed(1);
        if (payload?.text) text = payload.text;
        body.className = "figaro-md text-sm text-gray-800 leading-relaxed";
        paintMarkdown(body, text || "(no answer)");
        for (const [, row] of rows) {
          row.mark.replaceChildren(el("span", { class: "text-emerald-600 text-xs", text: "✓" }));
          row.label.className = "text-xs text-gray-400";
        }
        const callsign = payload?.persona?.callsign || "";
        if (payload?.persona) paintPersona(payload.persona);
        wrap.firstChild.replaceChildren(
          el("span", { class: "text-emerald-600 text-xs", text: "✓" }),
          el("span", {
            class: "text-xs text-gray-400",
            text: callsign ? `${callsign} · done in ${elapsed}s` : `Done in ${elapsed}s`,
          })
        );
        const metaNodes = [];
        if (callsign) {
          metaNodes.push(
            el("span", {
              class:
                "inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full border border-sky-200 bg-sky-50 text-sky-800",
              text: callsign,
              title: payload?.persona?.blurb || "",
            })
          );
        }
        if (payload?.taskLabel) {
          metaNodes.push(
            el("span", {
              class:
                "inline-flex items-center px-2 py-0.5 text-[11px] rounded-full border border-gray-200 bg-gray-50 text-gray-600",
              text: payload.taskLabel,
            })
          );
        }
        if (payload?.costUsd != null) {
          metaNodes.push(
            el("span", {
              class: "text-[11px] tabular-nums text-gray-500",
              text: `This turn ${formatSpend(payload.costUsd)}`,
              title: "API cost for this answer",
            })
          );
        }
        if (payload?.learned?.length) {
          metaNodes.push(
            el("span", {
              class: "text-[11px] text-emerald-700",
              text: `Playbook learned ${payload.learned.length} gap${payload.learned.length === 1 ? "" : "s"}`,
              title: payload.learned.map((l) => l.note).join(" · "),
            })
          );
        }
        meta.replaceChildren(...metaNodes);
        const attachments = payload?.attachments || [];
        const workbooks = attachments.filter((item) => item?.kind === "workbook" && item.documentId);
        const nodes = workbooks.map((book) => workbookCard(el, apiUrl, book));
        const docExtracts = extractsFromChatCitations(payload?.citations || []);
        if (docExtracts.length) {
          nodes.push(
            renderDocExtractsViewer({
              el,
              fetchJson,
              apiUrl,
              cleanText,
              extracts: docExtracts,
              contextTitle: "Cited sources",
              compact: true,
            })
          );
        }
        if (nodes.length) sources.replaceChildren(...nodes);
        else sources.replaceChildren();
        scroll();
      },
      fail(message) {
        clearInterval(tick);
        wrap.firstChild.replaceChildren(
          el("span", { class: "text-xs text-red-700", text: cleanText(message) || "Something went wrong" })
        );
        body.className = "figaro-md text-sm text-gray-800 leading-relaxed";
        if (text) paintMarkdown(body, text);
        scroll();
      },
    };
  }

  const input = el("textarea", {
    rows: 1,
    placeholder: "Ask anything — drop files here or use + to add documents…",
    class:
      "figaro-input flex-1 resize-none bg-transparent px-1 py-2 text-sm text-gray-900 placeholder:text-gray-400 max-h-40",
  });
  const attachButton = el("button", {
    type: "button",
    class:
      "shrink-0 w-9 h-9 rounded-full text-gray-500 text-xl leading-none border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 transition",
    text: "+",
    title: `Add documents to ${contextLabel(context)}`,
  });
  const sendButton = el("button", {
    type: "button",
    class:
      "shrink-0 w-9 h-9 rounded-full text-white text-base leading-none hover:opacity-90 disabled:opacity-30 transition",
    style: "background-color: rgb(192, 0, 0);",
    text: "↑",
    title: "Send",
  });

  input.addEventListener("input", () => {
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  });
  attachButton.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    attachButton.title = `Add documents to ${contextLabel(context)}`;
    filePicker.click();
  };
  sendButton.onclick = () => void send();

  async function send() {
    const message = input.value.trim();
    if (!message || busy) return;
    busy = true;
    sendButton.disabled = true;
    attachButton.disabled = true;
    input.value = "";
    input.style.height = "auto";

    if (thread.dataset.started !== "1") {
      thread.dataset.started = "1";
      thread.replaceChildren();
    }
    thread.appendChild(bubbleUser(message));
    const bubble = startAssistantBubble();
    thread.scrollTop = thread.scrollHeight;

    const body = {
      message,
      sessionId,
      scopeLevel: context.scopeLevel,
      operatorId: context.operatorId,
      assetId: context.assetId,
      taskType,
    };

    try {
      await streamChat(apiUrl, body, {
        onStep: (step) => bubble.step(step),
        onDelta: (chunk) => bubble.delta(chunk),
        onDone: (payload) => {
          sessionId = payload.sessionId || sessionId;
          if (payload.sessionCostUsd != null) paintSpend(payload.sessionCostUsd, payload.costUsd);
          else if (payload.costUsd != null) paintSpend(sessionSpend + Number(payload.costUsd || 0), payload.costUsd);
          bubble.finish(payload);
        },
        onFail: (payload) => bubble.fail(payload.error),
      });
    } catch (streamError) {
      // Streaming can be blocked by proxies; fall back to a single response.
      try {
        bubble.step({ key: "fallback", label: "Finishing the answer" });
        const payload = await fetchJson(apiUrl("/api/ai/chat/message"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        sessionId = payload.sessionId || sessionId;
        if (payload.sessionCostUsd != null) paintSpend(payload.sessionCostUsd, payload.costUsd);
        else if (payload.costUsd != null) paintSpend(sessionSpend + Number(payload.costUsd || 0), payload.costUsd);
        bubble.finish(payload);
      } catch (error) {
        bubble.fail(cleanText(error?.message) || cleanText(streamError?.message));
      }
    } finally {
      busy = false;
      sendButton.disabled = false;
      attachButton.disabled = false;
      void refreshRecent();
      input.focus();
    }
  }

  async function openSession(id) {
    try {
      const data = await fetchJson(apiUrl(`/api/ai/chat/sessions/${id}`));
      sessionId = data.session.id;
      paintSpend(data.session.costUsd || 0, 0);
      context = {
        scopeLevel: data.session.scopeLevel || "GLOBAL",
        operatorId: data.session.operatorId || null,
        assetId: data.session.assetId || null,
        label:
          contextOptions().find(
            (option) =>
              option.scopeLevel === (data.session.scopeLevel || "GLOBAL") &&
              (option.operatorId || null) === (data.session.operatorId || null) &&
              (option.assetId || null) === (data.session.assetId || null)
          )?.label || "Everything",
      };
      paintContextButton();
      void refreshContextFiles();
      thread.dataset.started = "1";
      thread.replaceChildren();
      for (const message of data.messages || []) {
        if (message.role === "user") {
          thread.appendChild(bubbleUser(message.content || ""));
          continue;
        }
        const body = el("div", { class: "figaro-md text-sm text-gray-800 leading-relaxed" });
        if (String(message.content || "").trim()) {
          paintMarkdown(body, message.content);
        } else {
          body.replaceChildren(
            el("div", {
              class: "rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-800",
              text: "No answer was returned for this turn.",
            })
          );
        }
        const citations = message.citations || [];
        const workbooks = citations.filter((citation) => citation?.kind === "workbook" && citation.documentId);
        const docExtracts = extractsFromChatCitations(citations);
        thread.appendChild(
          el("div", { class: "figaro-rise max-w-[92%] space-y-2" }, [
            body,
            ...workbooks.map((citation) =>
              workbookCard(el, apiUrl, {
                documentId: citation.documentId,
                title: citation.title,
                filename: citation.filename,
              })
            ),
            docExtracts.length
              ? renderDocExtractsViewer({
                  el,
                  fetchJson,
                  apiUrl,
                  cleanText,
                  extracts: docExtracts,
                  contextTitle: "Cited sources",
                  compact: true,
                })
              : null,
          ])
        );
      }
      jumpThreadToBottom();
    } catch (error) {
      alert(cleanText(error?.message) || "Could not open that chat");
    }
  }

  async function deleteSession(id, title) {
    try {
      const preview = await fetchJson(apiUrl(`/api/ai/chat/sessions/${id}/delete-preview`));
      const documents = preview.documents || [];
      if (!window.confirm(`Delete “${title || `Chat ${id}`}” and its message history?`)) return;

      let deleteDocuments = false;
      if (documents.length) {
        const lines = documents.map((document) => {
          const name = document.title || document.originalFilename || `Document ${document.id}`;
          const shared = Number(document.otherChatCount) > 0
            ? ` — also used by ${document.otherChatCount} other chat${document.otherChatCount === 1 ? "" : "s"}`
            : "";
          return `• ${name}${shared}`;
        });
        deleteDocuments = window.confirm(
          `This chat used the following library attachment${documents.length === 1 ? "" : "s"}:\n\n` +
            `${lines.join("\n")}\n\n` +
            "Also permanently remove these attachments from the Figaro document library?\n\n" +
            "OK = delete chat and attachments\nCancel = delete chat only"
        );
      }

      await fetchJson(apiUrl(`/api/ai/chat/sessions/${id}`), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deleteDocuments }),
      });
      if (Number(sessionId) === Number(id)) {
        sessionId = null;
        thread.dataset.started = "0";
        paintSpend(0);
        paintEmptyState();
      }
      await refreshRecent();
    } catch (error) {
      alert(cleanText(error?.message) || "Could not delete that chat");
    }
  }

  function recentRow(session, nested) {
    const title = session.title || `Chat ${session.id}`;
    return el(
      "div",
      { class: `group flex items-start gap-1 rounded-lg hover:bg-gray-50 ${nested ? "ml-2" : ""}` },
      [
        el("button", {
          type: "button",
          class: "min-w-0 flex-1 text-left px-2 py-1.5",
          onclick: () => void openSession(session.id),
          title,
        }, [
          el("div", { class: "text-sm text-gray-800 truncate", text: title }),
          el("div", {
            class: "text-[11px] tabular-nums text-gray-400",
            text: formatSpend(session.costUsd),
            title: "Chat spend",
          }),
        ]),
        el("button", {
          type: "button",
          class: "shrink-0 mt-1 px-1 leading-none text-base text-gray-300 hover:text-dae transition",
          text: "×",
          title: `Delete ${title}`,
          onclick: (event) => {
            event.stopPropagation();
            void deleteSession(session.id, session.title);
          },
        }),
      ]
    );
  }

  function recentHeading(text, nested) {
    return el("div", {
      class: `px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        nested ? "ml-2 text-gray-400" : "text-gray-500"
      }`,
      text,
    });
  }

  /** General first, then one block per operator with that operator's assets nested inside it. */
  function recentGroups(sessions) {
    const groups = [];
    const general = sessions.filter((s) => s.scopeLevel === "GLOBAL");
    if (general.length) groups.push({ title: "General", sessions: general, assets: [] });

    const operators = new Map();
    for (const session of sessions) {
      if (session.scopeLevel === "GLOBAL") continue;
      const key = session.groupOperatorId ? `op-${session.groupOperatorId}` : "unassigned";
      if (!operators.has(key)) {
        operators.set(key, {
          title: session.operatorName || (session.scopeLevel === "ASSET" ? "Assets" : "Operator"),
          sessions: [],
          assets: new Map(),
        });
      }
      const group = operators.get(key);
      if (session.scopeLevel === "ASSET" && session.assetId) {
        const label = [session.assetSerial ? `MSN ${session.assetSerial}` : `Asset ${session.assetId}`,
          session.assetRegistration]
          .filter(Boolean)
          .join(" · ");
        if (!group.assets.has(session.assetId)) group.assets.set(session.assetId, { label, sessions: [] });
        group.assets.get(session.assetId).sessions.push(session);
        continue;
      }
      group.sessions.push(session);
    }

    const ordered = [...operators.entries()]
      .map(([key, group]) => ({ key, ...group, assets: [...group.assets.values()] }))
      .sort((a, b) =>
        a.key === "unassigned" ? 1 : b.key === "unassigned" ? -1 : a.title.localeCompare(b.title)
      );
    return [...groups, ...ordered];
  }

  async function refreshRecent() {
    try {
      const data = await fetchJson(apiUrl("/api/ai/chat/sessions"));
      const sessions = data.sessions || [];
      if (!sessions.length) {
        recentList.replaceChildren(
          el("p", { class: "px-2 py-2 text-sm text-gray-500", text: "No chats yet." })
        );
        return;
      }
      const nodes = [];
      for (const group of recentGroups(sessions)) {
        nodes.push(recentHeading(group.title, false));
        for (const session of group.sessions) nodes.push(recentRow(session, false));
        for (const asset of group.assets || []) {
          nodes.push(recentHeading(asset.label, true));
          for (const session of asset.sessions) nodes.push(recentRow(session, true));
        }
      }
      recentList.replaceChildren(el("div", { class: "space-y-0.5" }, nodes));
    } catch {
      /* the rail is optional */
    }
  }

  contextButton.onclick = (event) => {
    event.stopPropagation();
    paintContextMenu();
    contextMenu.classList.toggle("hidden");
  };
  recentButton.onclick = (event) => {
    event.stopPropagation();
    contextMenu.classList.add("hidden");
    setRecentRail(recentRail.classList.contains("hidden"));
  };
  const closeMenus = () => {
    // The SPA swaps routes without unmount hooks; retire the listener with the view.
    if (!document.body.contains(thread)) {
      document.removeEventListener("click", closeMenus);
      return;
    }
    contextMenu.classList.add("hidden");
  };
  document.addEventListener("click", closeMenus);
  contextMenu.addEventListener("click", (event) => event.stopPropagation());

  const chatColumn = el("div", { class: "relative flex-1 min-w-0 flex flex-col" }, [
    filesPanel,
    thread,
    el("div", { class: "pt-3 pb-1 space-y-1.5" }, [
      el("div", { class: "flex items-center gap-2 px-1" }, [
        el("span", { class: "text-[11px] uppercase tracking-wide text-gray-400", text: "Task" }),
        taskSelect,
        personaFlag,
        el("span", {
          class: "ml-auto text-[11px] text-gray-400",
          text: "last turn / session",
        }),
      ]),
      el(
        "div",
        {
          class:
            "flex items-end gap-2 rounded-2xl bg-white px-3 py-1.5 shadow-sm ring-1 ring-gray-200 focus-within:ring-[rgb(192,0,0)]/35",
        },
        [attachButton, input, spendEl, sendButton]
      ),
    ]),
    dropOverlay,
  ]);

  filesButton.onclick = (event) => {
    event.stopPropagation();
    const opening = filesPanel.classList.contains("hidden");
    filesPanel.classList.toggle("hidden", !opening);
    if (opening) void refreshContextFiles();
  };
  filePicker.onchange = () => {
    const files = [...(filePicker.files || [])];
    filePicker.value = "";
    void uploadToContext(files);
  };

  // Counted because dragging over child elements fires enter/leave pairs.
  let dragDepth = 0;
  const draggingFiles = (event) =>
    [...(event.dataTransfer?.types || [])].includes("Files");
  const showDrop = () => {
    dropHint.textContent = `Drop files to add to ${contextLabel(context)}`;
    dropOverlay.classList.remove("hidden");
    dropOverlay.classList.add("flex");
  };
  const hideDrop = () => {
    dragDepth = 0;
    dropOverlay.classList.add("hidden");
    dropOverlay.classList.remove("flex");
  };
  chatColumn.addEventListener("dragenter", (event) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
    dragDepth += 1;
    showDrop();
  });
  chatColumn.addEventListener("dragover", (event) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  });
  chatColumn.addEventListener("dragleave", () => {
    dragDepth -= 1;
    if (dragDepth <= 0) hideDrop();
  });
  chatColumn.addEventListener("drop", (event) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
    hideDrop();
    void uploadToContext(event.dataTransfer?.files);
  });

  paintContextButton();
  paintEmptyState();
  paintFiles();

  host.replaceChildren(
    el("div", { class: "flex flex-col h-[calc(100vh-5rem)] min-h-[28rem]" }, [
      el("div", { class: "flex flex-wrap items-center gap-2 pb-3" }, [
        el("h1", { class: "text-xl font-semibold text-gray-900 tracking-tight mr-1", text: "Figaro" }),
        el("div", { class: "relative" }, [contextButton, contextMenu]),
        el("div", { class: "ml-auto flex items-center gap-2" }, [
          el("button", {
            type: "button",
            class: "px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:border-gray-300 transition",
            text: "New chat",
            onclick: () => {
              sessionId = null;
              thread.dataset.started = "0";
              paintSpend(0);
              paintEmptyState();
              input.focus();
            },
          }),
          recentButton,
          filesButton,
          el("a", {
            href: "#/figaro/playbooks",
            class: "px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:border-gray-300 transition",
            text: "Playbooks",
          }),
          el("a", {
            href: "#/figaro/documents",
            class: "px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white hover:border-gray-300 transition",
            text: "Documents",
          }),
        ]),
      ]),
      el("div", { class: "flex-1 min-h-0 flex" }, [
        recentRail,
        chatColumn,
      ]),
      filePicker,
    ])
  );

  input.focus();

  void fetchJson(apiUrl("/api/ai/workspace"))
    .then((data) => {
      workspace = data;
      paintEmptyState();
      paintContextButton();
    })
    .catch(() => {
      /* chat still works without the context list */
    });
  void fetchJson(apiUrl("/api/ai/personas"))
    .then((data) => {
      paintTaskOptions(data.taskTypes || DEFAULT_TASK_TYPES);
    })
    .catch(() => {
      paintTaskOptions(DEFAULT_TASK_TYPES);
    });
  void refreshRecent();
  void refreshContextFiles();
}

/* ─── documents ────────────────────────────────────────────────────── */

/** Serial numbers read better as MSN/ESN than as the raw asset type. */
function assetSerialLabel(assetType) {
  const type = String(assetType || "").toUpperCase();
  if (type === "AIRFRAME") return "MSN";
  if (type === "ENGINE") return "ESN";
  return type || "Asset";
}

async function uploadDocument({ apiUrl, fetchJson }, file, fields) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") fd.append(key, String(value));
  });
  return fetchJson(apiUrl("/api/ai/documents/upload"), { method: "POST", body: fd });
}

function renderDocumentsHome({ el, fetchJson, apiUrl, cleanText, host }) {
  let docsPollTimer = null;
  const listHost = el("div", { class: "text-sm text-gray-500", text: "Loading documents…" });
  const searchHits = el("div", {});
  const docTypes = [
    ["LEASE", "Lease"],
    ["LEASE_AMENDMENT", "Lease amendment"],
    ["CRS", "CRS — Certificate of Release to Service"],
    ["INVOICE", "Invoice"],
    ["PURCHASE_ORDER", "Purchase order"],
    ["OTHER_LEGAL", "Other legal"],
    ["CLAIM", "Claim"],
    ["MPD", "MPD"],
    ["UTILIZATION", "Utilization"],
    ["SCREENSHOT", "Screenshot"],
    ["WORKSCOPE", "Workscope"],
    ["EMAIL", "Email"],
    ["OTHER", "Other"],
  ];
  const queued = [];
  let classificationChain = Promise.resolve();
  let scopeOptions = { operators: [], assets: [] };

  const queueHost = el("div", { class: "space-y-2" });
  const uploadStatus = el("p", { class: "text-xs text-gray-500" });
  const fileInput = el("input", {
    type: "file",
    multiple: true,
    class: "hidden",
    accept: ".pdf,.xlsx,.xls,.xlsm,.docx,.png,.jpg,.jpeg,.txt,.eml,application/pdf",
  });
  const dropZone = el(
    "div",
    {
      class:
        "rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-5 py-8 text-center cursor-pointer hover:border-dae hover:bg-red-50/30 transition",
      onclick: () => fileInput.click(),
      ondragover: (event) => {
        event.preventDefault();
        dropZone.classList.add("border-dae", "bg-red-50");
      },
      ondragleave: () => dropZone.classList.remove("border-dae", "bg-red-50"),
      ondrop: (event) => {
        event.preventDefault();
        dropZone.classList.remove("border-dae", "bg-red-50");
        addFiles(event.dataTransfer?.files);
      },
    },
    [
      el("div", { class: "text-base font-medium text-gray-800", text: "Drop files here" }),
      el("div", { class: "mt-1 text-sm text-gray-500", text: "or click to browse · up to 25 files at a time" }),
      el("div", { class: "mt-1 text-xs text-gray-400", text: "PDF, XLSX, DOCX, images, text and email" }),
      fileInput,
    ]
  );

  const scopeSelect = el("select", { class: "w-full border border-gray-200 rounded-lg px-2 py-2 text-sm" }, [
    el("option", { value: "GLOBAL", text: "Global — all operators/assets", selected: true }),
    el("option", { value: "OPERATOR", text: "Operator" }),
    el("option", { value: "ASSET", text: "Asset" }),
  ]);
  const operatorSelect = el("select", { class: "w-full border border-gray-200 rounded-lg px-2 py-2 text-sm" });
  const assetSelect = el("select", { class: "w-full border border-gray-200 rounded-lg px-2 py-2 text-sm" });
  const operatorField = el("label", { class: "hidden text-sm space-y-1" }, [
    el("span", { class: "text-gray-600", text: "Operator" }),
    operatorSelect,
  ]);
  const assetField = el("label", { class: "hidden text-sm space-y-1" }, [
    el("span", { class: "text-gray-600", text: "Asset" }),
    assetSelect,
  ]);

  function fillScopeOptions() {
    operatorSelect.replaceChildren(
      el("option", { value: "", text: "Choose an operator…" }),
      ...(scopeOptions.operators || []).map((op) =>
        el("option", { value: String(op.id), text: op.name || op.slug || `Operator ${op.id}` })
      )
    );
    assetSelect.replaceChildren(
      el("option", { value: "", text: "Choose an asset…" }),
      ...(scopeOptions.assets || []).map((asset) => {
        const registration = asset.metadata?.registration ? ` · ${asset.metadata.registration}` : "";
        const operator = asset.operatorName ? ` · ${asset.operatorName}` : "";
        return el("option", {
          value: String(asset.id),
          text: `${assetSerialLabel(asset.assetType)} ${asset.serialNumber}${registration}${operator}`,
          "data-operator-id": asset.operatorId || "",
        });
      })
    );
  }

  scopeSelect.onchange = () => {
    const scope = scopeSelect.value;
    operatorField.classList.toggle("hidden", scope !== "OPERATOR");
    assetField.classList.toggle("hidden", scope !== "ASSET");
  };

  const uploadButton = el("button", {
    type: "button",
    disabled: true,
    class:
      "px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 disabled:opacity-40 disabled:cursor-not-allowed",
    text: "Upload all",
  });

  function refreshUploadButton() {
    const hasFiles = queued.length > 0;
    const classified = queued.every((item) => item.state !== "classifying");
    const confirmed = queued.every((item) => !item.needsConfirmation || item.confirmed);
    const scopeReady =
      scopeSelect.value === "GLOBAL" ||
      (scopeSelect.value === "OPERATOR" && Boolean(operatorSelect.value)) ||
      (scopeSelect.value === "ASSET" && Boolean(assetSelect.value));
    uploadButton.disabled = !hasFiles || !classified || !confirmed || !scopeReady;
    uploadButton.textContent = hasFiles ? `Upload ${queued.length} file${queued.length === 1 ? "" : "s"}` : "Upload all";
  }

  operatorSelect.onchange = refreshUploadButton;
  assetSelect.onchange = refreshUploadButton;
  scopeSelect.addEventListener("change", refreshUploadButton);

  function renderQueue() {
    queueHost.replaceChildren(
      ...queued.map((item) => {
        const typeSelect = el(
          "select",
          {
            class:
              "border rounded-lg px-2 py-1.5 text-xs " +
              (item.needsConfirmation && !item.confirmed ? "border-amber-400 bg-amber-50" : "border-gray-200"),
          },
          docTypes.map(([type, label]) =>
            el("option", { value: type, text: label, selected: type === item.docType })
          )
        );
        typeSelect.onchange = () => {
          item.docType = typeSelect.value;
          item.confirmed = true;
          item.needsConfirmation = false;
          renderQueue();
        };
        const remove = el("button", {
          type: "button",
          class: "text-gray-400 hover:text-red-700 text-lg leading-none",
          text: "×",
          title: "Remove",
          onclick: () => {
            const index = queued.indexOf(item);
            if (index >= 0) queued.splice(index, 1);
            renderQueue();
          },
        });
        const controls = [typeSelect];
        if (item.needsConfirmation && !item.confirmed) {
          controls.push(
            el("button", {
              type: "button",
              class: "px-2 py-1 text-xs font-medium border border-amber-300 bg-amber-50 text-amber-900 rounded-lg",
              text: "Confirm category",
              onclick: () => {
                item.confirmed = true;
                item.needsConfirmation = false;
                renderQueue();
              },
            })
          );
        }
        return el("div", { class: "rounded-lg border border-gray-200 p-3 flex flex-wrap gap-3 items-center" }, [
          el("div", { class: "min-w-0 flex-1" }, [
            el("div", { class: "text-sm font-medium text-gray-900 truncate", text: item.file.name }),
            el("div", {
              class: "text-xs " + (item.state === "error" ? "text-red-700" : "text-gray-500"),
              text:
                item.state === "classifying"
                  ? "Identifying the document type…"
                  : item.state === "uploading"
                    ? "Uploading…"
                    : item.state === "done"
                      ? `Uploaded · document #${item.documentId}`
                      : item.reason || `${Math.round((item.confidence || 0) * 100)}% confidence`,
            }),
          ]),
          el("div", { class: "flex flex-wrap gap-2 items-center" }, controls),
          remove,
        ]);
      })
    );
    refreshUploadButton();
  }

  async function classifyItem(item) {
    const body = new FormData();
    body.append("file", item.file, item.file.name);
    try {
      const result = await fetchJson(apiUrl("/api/ai/documents/classify"), { method: "POST", body });
      item.docType = result.docType || "OTHER";
      item.confidence = Number(result.confidence) || 0;
      item.reason = result.reason || "Categorised automatically";
      item.needsConfirmation = Boolean(result.needsConfirmation);
      item.confirmed = !item.needsConfirmation;
      item.state = "ready";
    } catch (error) {
      const name = String(item.file?.name || "").toLowerCase();
      let docType = "OTHER";
      if (/amend|supplement|side.?letter/.test(name)) docType = "LEASE_AMENDMENT";
      else if (/lease|lessee|lessor|agreement/.test(name)) docType = "LEASE";
      else if (/legal|contract/.test(name)) docType = "OTHER_LEGAL";
      item.docType = docType;
      item.reason =
        docType !== "OTHER"
          ? "Type from filename — confirm if needed"
          : cleanText(error?.message) || "Could not categorise — choose a type";
      item.needsConfirmation = true;
      item.confirmed = false;
      item.state = docType !== "OTHER" ? "ready" : "error";
    }
    renderQueue();
  }

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []).slice(0, Math.max(0, 25 - queued.length));
    for (const file of incoming) {
      if (queued.some((item) => item.file.name === file.name && item.file.size === file.size)) continue;
      const item = {
        file,
        docType: "OTHER",
        confidence: 0,
        reason: "",
        needsConfirmation: false,
        confirmed: false,
        state: "classifying",
      };
      queued.push(item);
      // Keep large drag-and-drop batches from firing many simultaneous requests.
      classificationChain = classificationChain.then(() => classifyItem(item));
    }
    renderQueue();
  }

  fileInput.onchange = () => {
    addFiles(fileInput.files);
    fileInput.value = "";
  };

  uploadButton.onclick = async () => {
    const scopeLevel = scopeSelect.value || "GLOBAL";
    if (scopeLevel === "OPERATOR" && !operatorSelect.value) {
      alert("Choose an operator.");
      return;
    }
    if (scopeLevel === "ASSET" && !assetSelect.value) {
      alert("Choose an asset.");
      return;
    }
    uploadButton.disabled = true;
    let completed = 0;
    let failed = 0;
    for (const item of queued) {
      if (item.state === "done") continue;
      item.state = "uploading";
      renderQueue();
      const selectedAsset = assetSelect.selectedOptions?.[0];
      const operatorId =
        scopeLevel === "OPERATOR"
          ? operatorSelect.value
          : scopeLevel === "ASSET"
            ? selectedAsset?.getAttribute("data-operator-id") || ""
            : "";
      try {
        const result = await uploadDocument({ apiUrl, fetchJson }, item.file, {
          docType: item.docType,
          title: item.file.name,
          scopeLevel,
          operatorId,
          assetId: scopeLevel === "ASSET" ? assetSelect.value : "",
        });
        item.state = "done";
        item.documentId = result.documentId;
        item.reason = result.reused ? "Already indexed" : "Queued for indexing";
        completed += 1;
      } catch (error) {
        item.state = "error";
        item.reason = cleanText(error?.message) || "Upload failed";
        failed += 1;
      }
      renderQueue();
    }
    uploadStatus.textContent = `${completed} uploaded${failed ? ` · ${failed} failed` : ""}.`;
    uploadStatus.className = `text-xs ${failed ? "text-red-700" : "text-emerald-700"}`;
    refreshUploadButton();
    if (completed) {
      queued.splice(0, queued.length, ...queued.filter((item) => item.state !== "done"));
      renderQueue();
      void loadLibrary({ soft: true });
    }
  };

  const form = el("div", { class: "space-y-4" }, [
    dropZone,
    el("div", { class: "grid grid-cols-1 sm:grid-cols-3 gap-3" }, [
      el("label", { class: "block text-sm space-y-1" }, [
        el("span", { class: "text-gray-600", text: "Scope" }),
        scopeSelect,
      ]),
      operatorField,
      assetField,
    ]),
    queueHost,
    el("div", { class: "flex flex-wrap gap-3 items-center" }, [uploadButton, uploadStatus]),
  ]);

  let allDocs = [];
  let libraryReady = false;
  const collapsedKeys = new Set();
  const libraryFilter = { scopeKey: "ALL", q: "" };
  let filterDebounce = null;

  const libraryScopeSelect = el("select", {
    class: "w-full border border-gray-200 rounded-lg px-2 py-2 text-sm",
    title: "Filter library and text search by asset or operator",
  });
  const libraryKeywordInput = el("input", {
    type: "search",
    class: "w-full border border-gray-200 rounded-lg px-2 py-2 text-sm",
    placeholder: "Filter by filename, title, type, MSN…",
  });
  const libraryCountEl = el("span", { class: "text-xs text-gray-500", text: "" });

  function fillLibraryScopeOptions() {
    const current = libraryFilter.scopeKey;
    const options = [el("option", { value: "ALL", text: "All documents" }), el("option", { value: "GLOBAL", text: "Global only" })];
    for (const op of scopeOptions.operators || []) {
      options.push(
        el("option", {
          value: `operator:${op.id}`,
          text: `Operator · ${op.name || op.slug || op.id}`,
        })
      );
    }
    for (const asset of scopeOptions.assets || []) {
      const registration = asset.metadata?.registration ? ` · ${asset.metadata.registration}` : "";
      const operator = asset.operatorName ? ` · ${asset.operatorName}` : "";
      options.push(
        el("option", {
          value: `asset:${asset.id}`,
          text: `${assetSerialLabel(asset.assetType)} ${asset.serialNumber}${registration}${operator}`,
        })
      );
    }
    libraryScopeSelect.replaceChildren(...options);
    if ([...libraryScopeSelect.options].some((o) => o.value === current)) {
      libraryScopeSelect.value = current;
    } else {
      libraryFilter.scopeKey = "ALL";
      libraryScopeSelect.value = "ALL";
    }
  }

  function scopeQueryParams() {
    const key = libraryFilter.scopeKey || "ALL";
    if (key === "GLOBAL") return { scopeLevel: "GLOBAL" };
    if (key.startsWith("asset:")) return { assetId: key.slice(6) };
    if (key.startsWith("operator:")) return { operatorId: key.slice(9) };
    return {};
  }

  function docMatchesScope(doc) {
    const key = libraryFilter.scopeKey || "ALL";
    if (key === "ALL") return true;
    if (key === "GLOBAL") return doc.scopeLevel === "GLOBAL";
    if (key.startsWith("asset:")) {
      return Number(doc.assetId) === Number(key.slice(6));
    }
    if (key.startsWith("operator:")) {
      const opId = Number(key.slice(9));
      if (Number(doc.operatorId) === opId) return true;
      if (doc.assetId) {
        const asset = (scopeOptions.assets || []).find((a) => Number(a.id) === Number(doc.assetId));
        if (asset && Number(asset.operatorId) === opId) return true;
      }
      return false;
    }
    return true;
  }

  function docMatchesKeyword(doc) {
    const q = String(libraryFilter.q || "").trim().toLowerCase();
    if (!q) return true;
    const tokens = q.split(/\s+/).filter(Boolean);
    const hay = [
      doc.title,
      doc.originalFilename,
      doc.docType,
      doc.operatorName,
      doc.assetSerial,
      doc.assetMetadata?.registration,
      doc.status,
      String(doc.id || ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return tokens.every((token) => hay.includes(token));
  }

  function filteredDocs() {
    return allDocs.filter((doc) => docMatchesScope(doc) && docMatchesKeyword(doc));
  }

  libraryScopeSelect.onchange = () => {
    libraryFilter.scopeKey = libraryScopeSelect.value || "ALL";
    paintLibrary();
  };
  libraryKeywordInput.addEventListener("input", () => {
    libraryFilter.q = libraryKeywordInput.value || "";
    if (filterDebounce) clearTimeout(filterDebounce);
    filterDebounce = setTimeout(() => paintLibrary(), 120);
  });
  libraryKeywordInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      libraryFilter.q = libraryKeywordInput.value || "";
      paintLibrary();
      void runTextSearch();
    }
  });

  async function runTextSearch() {
    const q = String(libraryKeywordInput.value || "").trim();
    if (!q) {
      searchHits.replaceChildren(
        el("p", {
          class: "text-sm text-gray-500",
          text: "Type a keyword to filter the library, or Search text to look inside indexed pages.",
        })
      );
      return;
    }
    libraryFilter.q = q;
    paintLibrary();
    searchHits.replaceChildren(el("p", { class: "text-sm text-gray-500", text: "Searching document text…" }));
    try {
      const params = new URLSearchParams({ q, limit: "40", ...scopeQueryParams() });
      const data = await fetchJson(apiUrl(`/api/ai/search?${params.toString()}`));
      const hits = data.hits || [];
      searchHits.replaceChildren(
        hits.length
          ? el("div", { class: "space-y-2" }, [
              el("p", {
                class: "text-xs text-gray-500",
                text: `${hits.length} text hit${hits.length === 1 ? "" : "s"} in ${
                  libraryFilter.scopeKey === "ALL" ? "all documents" : "the selected filter"
                }`,
              }),
              el(
                "ul",
                { class: "divide-y divide-gray-100 text-sm" },
                hits.map((h) =>
                  el("li", { class: "py-2 space-y-1" }, [
                    el("div", { class: "flex flex-wrap justify-between gap-2" }, [
                      el("a", {
                        href: `#/figaro/documents/${h.documentId}?page=${h.pageStart || 1}&q=${encodeURIComponent(q)}`,
                        class: "font-medium text-dae hover:underline",
                        text: `${h.title || h.originalFilename || `#${h.documentId}`} · p.${h.pageStart}${
                          h.pageEnd && h.pageEnd !== h.pageStart ? `–${h.pageEnd}` : ""
                        }`,
                      }),
                      el("span", { class: "text-xs text-gray-500", text: h.docType || "" }),
                    ]),
                    (() => {
                      const d = el("div", { class: "text-xs text-gray-700 whitespace-pre-wrap" });
                      d.innerHTML = highlightHtml((h.excerpt || h.content || "").slice(0, 500), q);
                      return d;
                    })(),
                  ])
                )
              ),
            ])
          : el("p", {
              class: "text-sm text-gray-500",
              text: "No indexed-text hits in this filter. The library list above still shows filename matches.",
            })
      );
    } catch (e) {
      searchHits.replaceChildren(
        el("p", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
      );
    }
  }

  const filterBar = el("div", { class: "space-y-3" }, [
    el("div", { class: "grid grid-cols-1 sm:grid-cols-2 gap-3" }, [
      el("label", { class: "block text-sm space-y-1" }, [
        el("span", { class: "text-gray-600", text: "Filter by asset / operator" }),
        libraryScopeSelect,
      ]),
      el("label", { class: "block text-sm space-y-1" }, [
        el("span", { class: "text-gray-600", text: "Keyword" }),
        libraryKeywordInput,
      ]),
    ]),
    el("div", { class: "flex flex-wrap gap-2 items-center" }, [
      el("button", {
        type: "button",
        class: "px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "Search text",
        title: "Search inside indexed page text, limited to the filter above",
        onclick: () => void runTextSearch(),
      }),
      el("button", {
        type: "button",
        class: "px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50",
        text: "Clear",
        onclick: () => {
          libraryFilter.scopeKey = "ALL";
          libraryFilter.q = "";
          libraryScopeSelect.value = "ALL";
          libraryKeywordInput.value = "";
          searchHits.replaceChildren();
          paintLibrary();
        },
      }),
      libraryCountEl,
    ]),
    searchHits,
  ]);

  host.replaceChildren(
    el("div", { class: "flex flex-wrap items-center gap-2" }, [
      el("a", { href: "#/figaro", class: "text-sm text-dae hover:underline", text: "← Chat" }),
      el("h1", { class: "text-xl font-semibold text-gray-900 tracking-tight", text: "Documents" }),
    ]),
    panel(el, "Add files", [form]),
    panel(el, "Find documents", [filterBar]),
    panel(el, "Library", [listHost])
  );

  void fetchJson(apiUrl("/api/ai/scope-options"))
    .then((data) => {
      scopeOptions = data;
      fillScopeOptions();
      fillLibraryScopeOptions();
      refreshUploadButton();
      if (libraryReady) paintLibrary();
    })
    .catch((error) => {
      uploadStatus.textContent = `Could not load operator/asset lists: ${cleanText(error?.message) || error}`;
      uploadStatus.className = "text-xs text-red-700";
    });

  function assetLabel(doc) {
    const serial = doc.assetSerial || "";
    const registration = doc.assetMetadata?.registration || "";
    if (serial && registration) return `${serial} · ${registration}`;
    return serial || registration || `Asset #${doc.assetId}`;
  }

  function softReload() {
    void loadLibrary({ soft: true });
  }

  function docRow(doc) {
    return el("div", { class: "flex flex-wrap items-start gap-2 py-2 border-b border-gray-100 last:border-0" }, [
      el("div", { class: "min-w-0 flex-1 space-y-1" }, [
        el("a", {
          href: `#/figaro/documents/${doc.id}`,
          class: "text-sm font-medium text-gray-900 hover:text-dae hover:underline",
          text: doc.title || doc.originalFilename || `#${doc.id}`,
        }),
        el("div", { class: "flex flex-wrap gap-1.5 items-center" }, [
          el("span", { class: "text-[11px] text-gray-500", text: doc.docType || "—" }),
          statusBadge(el, doc.status),
          doc.status === "PARSING" || doc.status === "UPLOADED"
            ? null
            : ocrBadge(el, doc.ocrStatus, doc.emptyPages, doc.pageCount),
          el("span", {
            class: "text-[11px] text-gray-400",
            text:
              doc.status === "PARSING" || doc.status === "UPLOADED"
                ? ""
                : `${doc.pageCount ?? 0} pages · ${doc.chunkCount ?? 0} chunks`,
          }),
        ]),
        ingestProgressBar(el, doc),
      ]),
      el("div", { class: "flex flex-wrap gap-1.5" }, [
        el("a", {
          href: `#/figaro/documents/${doc.id}`,
          class: "px-2 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50",
          text: "Open",
        }),
        el("button", {
          type: "button",
          class: "px-2 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50",
          text: "Re-index",
          onclick: async () => {
            try {
              await fetchJson(apiUrl(`/api/ai/documents/${doc.id}/reindex`), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: "{}",
              });
              alert("Re-queued for indexing.");
              setTimeout(softReload, 1200);
            } catch (e) {
              alert(cleanText(e?.message) || "Reindex failed");
            }
          },
        }),
        el("button", {
          type: "button",
          class: "px-2 py-1 text-xs rounded-lg border border-red-200 text-red-700 hover:bg-red-50",
          text: "Delete",
          onclick: async () => {
            if (!window.confirm(`Delete “${doc.title || doc.originalFilename || doc.id}”? This cannot be undone.`)) {
              return;
            }
            try {
              await fetchJson(apiUrl(`/api/ai/documents/${doc.id}`), { method: "DELETE" });
              softReload();
            } catch (e) {
              alert(cleanText(e?.message) || "Delete failed");
            }
          },
        }),
      ]),
    ]);
  }

  function groupSection(sectionKey, title, docs) {
    const openByDefault = !collapsedKeys.has(sectionKey);
    const body = el("div", { class: openByDefault ? "space-y-0 pl-1" : "hidden space-y-0 pl-1" });
    const chevron = el("span", { class: "text-gray-400 w-4", text: openByDefault ? "▾" : "▸" });
    const header = el(
      "button",
      {
        type: "button",
        class:
          "w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 text-left sticky top-0 bg-white",
        onclick: () => {
          const hidden = body.classList.toggle("hidden");
          chevron.textContent = hidden ? "▸" : "▾";
          if (hidden) collapsedKeys.add(sectionKey);
          else collapsedKeys.delete(sectionKey);
        },
      },
      [
        chevron,
        el("span", { class: "text-sm font-semibold text-gray-900", text: title }),
        el("span", { class: "text-xs text-gray-400", text: `${docs.length}` }),
      ]
    );
    body.replaceChildren(...docs.map((d) => docRow(d)));
    return el("div", { class: "border border-gray-200 rounded-xl overflow-hidden" }, [header, body]);
  }

  function paintLibrary() {
    const docs = filteredDocs();
    const total = allDocs.length;
    libraryCountEl.textContent = total
      ? `Showing ${docs.length} of ${total}`
      : "No documents yet";

    if (!total) {
      listHost.replaceChildren(el("p", { class: "text-sm text-gray-500", text: "No documents yet." }));
      return;
    }
    if (!docs.length) {
      listHost.replaceChildren(
        el("p", {
          class: "text-sm text-gray-500",
          text: "No documents match this filter. Clear the keyword or widen the asset selection.",
        })
      );
      return;
    }

    const globalDocs = docs.filter((d) => d.scopeLevel === "GLOBAL");
    const byOperator = new Map();
    const byAsset = new Map();
    for (const doc of docs) {
      if (doc.scopeLevel === "OPERATOR" && doc.operatorId) {
        const key = doc.operatorId;
        if (!byOperator.has(key)) byOperator.set(key, { name: doc.operatorName || `Operator ${key}`, docs: [] });
        byOperator.get(key).docs.push(doc);
      } else if (doc.scopeLevel === "ASSET" && doc.assetId) {
        const key = doc.assetId;
        if (!byAsset.has(key)) {
          byAsset.set(key, {
            name: `${assetLabel(doc)}${doc.operatorName ? ` · ${doc.operatorName}` : ""}`,
            docs: [],
          });
        }
        byAsset.get(key).docs.push(doc);
      }
    }

    const sections = [];
    if (globalDocs.length) sections.push(groupSection("global", "Global", globalDocs));
    for (const [key, group] of [...byOperator.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name))) {
      sections.push(groupSection(`operator:${key}`, `Operator · ${group.name}`, group.docs));
    }
    for (const [key, group] of [...byAsset.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name))) {
      sections.push(groupSection(`asset:${key}`, `Asset · ${group.name}`, group.docs));
    }
    listHost.replaceChildren(el("div", { class: "space-y-3" }, sections));
  }

  async function loadLibrary({ soft = false } = {}) {
    if (!soft || !libraryReady) {
      listHost.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading documents…" }));
    }
    try {
      const data = await fetchJson(apiUrl("/api/ai/documents"));
      if (!document.body.contains(listHost)) return;
      allDocs = data.documents || [];
      libraryReady = true;
      paintLibrary();
      const busy = allDocs.some((d) => d.status === "PARSING" || d.status === "UPLOADED");
      if (docsPollTimer) clearTimeout(docsPollTimer);
      docsPollTimer = busy
        ? setTimeout(() => {
            if (document.body.contains(listHost)) void loadLibrary({ soft: true });
          }, 4000)
        : null;
    } catch (e) {
      if (!soft || !libraryReady) {
        listHost.replaceChildren(el("p", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
      }
    }
  }

  void loadLibrary();
}

function renderDocumentDetail({ el, fetchJson, apiUrl, cleanText, host, docId }) {
  const params = new URLSearchParams(String(location.hash.split("?")[1] || ""));
  const startPage = Number(params.get("page") || 1) || 1;
  const highlightQ = params.get("q") || "";

  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading document…" }));

  (async () => {
    try {
      const data = await fetchJson(apiUrl(`/api/ai/documents/${docId}`));
      const doc = data.document;
      const pageSel = el("select", { class: "border border-gray-200 rounded-lg px-2 py-1.5 text-sm" });
      for (const p of data.pages || []) {
        pageSel.appendChild(
          el("option", {
            value: String(p.pageNumber),
            text: `Page ${p.pageNumber}${p.sheetName ? ` · ${p.sheetName}` : ""}`,
            selected: p.pageNumber === startPage ? "selected" : undefined,
          })
        );
      }
      if (!(data.pages || []).length) {
        pageSel.appendChild(el("option", { value: "1", text: "No pages yet" }));
      }

      const textHost = el("div", {
        class:
          "text-sm whitespace-pre-wrap font-mono leading-relaxed text-gray-800 max-h-[32rem] overflow-y-auto border border-gray-100 rounded-lg p-3 bg-gray-50",
      });
      const iframe = el("iframe", {
        class: "w-full h-[32rem] border border-gray-200 rounded-lg bg-white",
        title: "Original file",
        src: `${apiUrl(`/api/ai/documents/${docId}/file`)}#page=${startPage}`,
      });

      async function loadPage(n) {
        textHost.textContent = "Loading…";
        try {
          const pg = await fetchJson(apiUrl(`/api/ai/documents/${docId}/pages/${n}`));
          const rows = pg.pages || [];
          const text = rows.map((p) => p.text).join("\n\n---\n\n");
          const meta = rows[0];
          const metaLine = meta
            ? [
                meta.extractionMethod || meta.method
                  ? `Extract: ${meta.extractionMethod || meta.method}`
                  : null,
                meta.quality != null ? `quality ${Number(meta.quality).toFixed(2)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : "";
          textHost.replaceChildren();
          if (metaLine) {
            textHost.appendChild(
              el("div", { class: "text-[11px] text-gray-500 mb-2 font-sans", text: metaLine })
            );
          }
          const body = el("div", { class: "whitespace-pre-wrap" });
          body.innerHTML = highlightHtml(text || "(empty)", highlightQ);
          textHost.appendChild(body);
          const mark = textHost.querySelector("mark");
          if (mark) {
            mark.scrollIntoView({ block: "center", behavior: "smooth" });
          }
          if (/\.pdf$/i.test(doc.originalFilename || "") || String(doc.mimeType || "").includes("pdf")) {
            iframe.src = `${apiUrl(`/api/ai/documents/${docId}/file`)}#page=${n}`;
          }
        } catch (e) {
          textHost.textContent = cleanText(e?.message) || String(e);
        }
      }

      pageSel.onchange = () => loadPage(Number(pageSel.value) || 1);

      host.replaceChildren(
        el("div", { class: "flex flex-wrap items-center gap-2 text-sm" }, [
          el("a", { href: "#/figaro/documents", class: "text-dae hover:underline", text: "← Library" }),
          statusBadge(el, doc.status),
          doc.status === "PARSING" || doc.status === "UPLOADED"
            ? null
            : ocrBadge(el, doc.ocrStatus, null, doc.pageCount),
          el("span", {
            class: "text-gray-500",
            text:
              doc.status === "PARSING" || doc.status === "UPLOADED"
                ? "Indexing in progress"
                : `${doc.docType} · ${doc.pageCount ?? 0} pages`,
          }),
          el("button", {
            type: "button",
            class: "ml-auto px-2 py-1 border border-gray-200 rounded-lg hover:bg-gray-50",
            text: "Re-index",
            onclick: async () => {
              try {
                const r = await fetchJson(apiUrl(`/api/ai/documents/${docId}/reindex`), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: "{}",
                });
                alert(`Re-queued · job ${r.jobId}`);
                setTimeout(() => location.reload(), 1500);
              } catch (e) {
                alert(cleanText(e?.message) || "Reindex failed");
              }
            },
          }),
        ]),
        panel(el, doc.title || doc.originalFilename || `Document #${docId}`, [
          ingestProgressBar(el, doc),
          el("p", {
            class: "text-xs text-gray-500 font-mono",
            text: `${doc.originalFilename || ""} · ${doc.mimeType || ""} · sha ${String(doc.sha256 || "").slice(0, 16)}…`,
          }),
          el("div", { class: "flex flex-wrap items-center gap-2" }, [
            el("span", { class: "text-sm text-gray-600", text: "Citation page:" }),
            pageSel,
            highlightQ
              ? el("span", {
                  class: "text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5",
                  text: `Highlight: ${highlightQ}`,
                })
              : null,
          ]),
          el("div", { class: "grid grid-cols-1 lg:grid-cols-2 gap-3 pt-2" }, [
            el("div", { class: "space-y-1" }, [
              el("div", { class: "text-xs font-medium text-gray-500 uppercase tracking-wide", text: "Extracted text" }),
              textHost,
            ]),
            el("div", { class: "space-y-1" }, [
              el("div", { class: "text-xs font-medium text-gray-500 uppercase tracking-wide", text: "Original" }),
              /\.pdf$/i.test(doc.originalFilename || "") || String(doc.mimeType || "").includes("pdf")
                ? iframe
                : el("a", {
                    href: apiUrl(`/api/ai/documents/${docId}/file`),
                    target: "_blank",
                    class: "text-sm text-dae hover:underline",
                    text: "Download / open original file",
                  }),
            ]),
          ]),
        ])
      );
      await loadPage(startPage);
    } catch (e) {
      host.replaceChildren(el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
    }
  })();
}

export function renderFigaroRoute({ el, fetchJson, apiUrl, cleanText, routeKey }) {
  const normalized = normalizeFigaroRouteKey(routeKey);
  if (normalized !== routeKey && typeof window !== "undefined") {
    const hash = String(window.location.hash || "");
    const qs = hash.includes("?") ? hash.slice(hash.indexOf("?")) : "";
    history.replaceState(null, "", `#/${normalized}${qs}`);
  }
  routeKey = normalized;
  ensureStyles();
  const raw = String(routeKey || "figaro").replace(/^figaro\/?/, "");
  const parts = raw.split("/").filter(Boolean);
  const sub = parts[0] || "chat";
  const docId = sub === "documents" && parts[1] ? Number(parts[1]) : null;
  const playbookId = sub === "playbooks" && parts[1] ? parts[1] : null;

  const host = el("div", { class: "space-y-4" });

  if (sub === "workscopes") {
    return renderWorkscopesRoute({ el, fetchJson, apiUrl, cleanText, routeKey });
  }
  if (sub === "lease-returns") {
    return renderLeaseReturnsRoute({ el, fetchJson, apiUrl, cleanText, routeKey });
  }
  if (sub === "invoice-audit") {
    return renderInvoiceAuditRoute({ el, fetchJson, apiUrl, cleanText, routeKey });
  }
  if (sub === "documents" && docId) renderDocumentDetail({ el, fetchJson, apiUrl, cleanText, host, docId });
  else if (sub === "documents") renderDocumentsHome({ el, fetchJson, apiUrl, cleanText, host });
  else if (sub === "playbooks") renderPlaybooks({ el, fetchJson, apiUrl, cleanText, host, playbookId });
  else renderChat({ el, fetchJson, apiUrl, cleanText, host });

  return host;
}

function playbookStatusTone(status) {
  const s = String(status || "");
  if (s === "ACTIVE") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (s === "DRAFT") return "bg-amber-50 text-amber-900 border-amber-200";
  return "bg-gray-50 text-gray-600 border-gray-200";
}

function renderPlaybooks({ el, fetchJson, apiUrl, cleanText, host, playbookId }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading playbooks…" }));

  async function loadList() {
    const data = await fetchJson(apiUrl("/api/ai/playbooks?includeArchived=1"));
    const playbooks = data.playbooks || [];
    if (playbookId) {
      const match = playbooks.find((p) => String(p.id) === String(playbookId) || p.slug === playbookId);
      if (match) {
        await paintEditor(match.id);
        return;
      }
    }
    host.replaceChildren(
      el("div", { class: "space-y-4" }, [
        el("div", { class: "flex flex-wrap items-center gap-3" }, [
          el("a", { href: "#/figaro", class: "text-sm text-dae hover:underline", text: "← Chat" }),
          el("h1", { class: "text-xl font-semibold text-gray-900 tracking-tight", text: "Playbooks" }),
        ]),
        el("p", {
          class: "text-sm text-gray-600 max-w-3xl",
          text:
            "Expected outcomes for core processes. The standard skeleton is here; aircraft-specific numbers and variation still come from the documents in the chat context.",
        }),
        el(
          "div",
          { class: "grid gap-3 md:grid-cols-2" },
          playbooks.map((pb) =>
            el(
              "button",
              {
                type: "button",
                class:
                  "text-left rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-gray-300 transition space-y-2",
                onclick: () => void paintEditor(pb.id),
              },
              [
                el("div", { class: "flex items-start justify-between gap-2" }, [
                  el("div", { class: "text-base font-semibold text-gray-900", text: pb.title }),
                  el("span", {
                    class: `shrink-0 inline-flex px-2 py-0.5 text-[11px] font-medium border rounded ${playbookStatusTone(pb.status)}`,
                    text: pb.status,
                  }),
                ]),
                el("p", { class: "text-sm text-gray-600", text: pb.summary || "No summary yet." }),
                el("div", {
                  class: "text-[11px] text-gray-400",
                  text: `${Number(pb.bodyChars || 0).toLocaleString()} characters · triggers: ${(pb.triggerKeywords || []).slice(0, 4).join(", ") || "—"}`,
                }),
              ]
            )
          )
        ),
      ])
    );
  }

  async function paintEditor(id) {
    host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading playbook…" }));
    try {
      const data = await fetchJson(apiUrl(`/api/ai/playbooks/${id}`));
      const pb = data.playbook;
      let learnings = data.learnings || [];
      const titleInput = el("input", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
        value: pb.title || "",
      });
      const summaryInput = el("textarea", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm min-h-[4.5rem]",
        text: pb.summary || "",
      });
      const keywordsInput = el("input", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm",
        value: (pb.triggerKeywords || []).join(", "),
        placeholder: "eol, true-up, redelivery…",
      });
      const statusSelect = el("select", { class: "border border-gray-200 rounded-lg px-2 py-2 text-sm" }, [
        el("option", { value: "ACTIVE", text: "ACTIVE — used in matching chats", selected: pb.status === "ACTIVE" }),
        el("option", { value: "DRAFT", text: "DRAFT — not applied yet", selected: pb.status === "DRAFT" }),
        el("option", { value: "ARCHIVED", text: "ARCHIVED", selected: pb.status === "ARCHIVED" }),
      ]);
      const bodyInput = el("textarea", {
        class: "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono min-h-[28rem] leading-relaxed",
        text: pb.body || "",
      });
      const saveStatus = el("span", { class: "text-xs text-gray-500" });
      const learningsHost = el("div", { class: "space-y-2" });

      function paintLearnings() {
        if (!learnings.length) {
          learningsHost.replaceChildren(
            el("p", {
              class: "text-sm text-gray-500",
              text: "No chat learnings yet. Gaps Figaro finds in answers are saved here automatically.",
            })
          );
          return;
        }
        learningsHost.replaceChildren(
          ...learnings.map((item) =>
            el("div", { class: "flex items-start gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2" }, [
              el("div", { class: "min-w-0 flex-1" }, [
                el("div", { class: "text-sm text-gray-800", text: item.note }),
                el("div", {
                  class: "text-[11px] text-gray-400 mt-0.5",
                  text: [
                    item.source || "chat",
                    item.createdBy,
                    item.createdAt ? new Date(item.createdAt).toLocaleString() : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                }),
              ]),
              el("button", {
                type: "button",
                class: "shrink-0 text-xs text-gray-400 hover:text-dae",
                text: "Remove",
                title: "Delete this learning",
                onclick: async () => {
                  if (!confirm("Remove this learning from the playbook?")) return;
                  try {
                    await fetchJson(apiUrl(`/api/ai/playbooks/${pb.id}/learnings/${item.id}`), {
                      method: "DELETE",
                    });
                    learnings = learnings.filter((row) => row.id !== item.id);
                    paintLearnings();
                  } catch (error) {
                    alert(cleanText(error?.message) || "Could not remove learning");
                  }
                },
              }),
            ])
          )
        );
      }
      paintLearnings();

      const saveButton = el("button", {
        type: "button",
        class: "px-3 py-2 text-sm font-medium text-white rounded-lg bg-dae hover:opacity-90",
        text: "Save playbook",
        onclick: async () => {
          saveButton.disabled = true;
          saveStatus.textContent = "Saving…";
          try {
            await fetchJson(apiUrl(`/api/ai/playbooks/${pb.id}`), {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                title: titleInput.value,
                summary: summaryInput.value,
                body: bodyInput.value,
                status: statusSelect.value,
                triggerKeywords: keywordsInput.value
                  .split(",")
                  .map((k) => k.trim())
                  .filter(Boolean),
              }),
            });
            saveStatus.textContent = "Saved";
            saveStatus.className = "text-xs text-emerald-700";
            window.location.hash = `#/figaro/playbooks/${pb.slug || pb.id}`;
          } catch (error) {
            saveStatus.textContent = cleanText(error?.message) || "Save failed";
            saveStatus.className = "text-xs text-red-700";
          } finally {
            saveButton.disabled = false;
          }
        },
      });

      host.replaceChildren(
        el("div", { class: "space-y-4 max-w-4xl" }, [
          el("div", { class: "flex flex-wrap items-center gap-3" }, [
            el("button", {
              type: "button",
              class: "text-sm text-dae hover:underline",
              text: "← All playbooks",
              onclick: () => {
                window.location.hash = "#/figaro/playbooks";
                void loadList();
              },
            }),
            el("span", { class: "text-xs text-gray-400 font-mono", text: pb.slug }),
          ]),
          el("div", { class: "space-y-1" }, [
            el("label", { class: "text-xs font-semibold uppercase tracking-wide text-gray-500", text: "Title" }),
            titleInput,
          ]),
          el("div", { class: "space-y-1" }, [
            el("label", { class: "text-xs font-semibold uppercase tracking-wide text-gray-500", text: "Summary" }),
            summaryInput,
          ]),
          el("div", { class: "grid gap-3 sm:grid-cols-2" }, [
            el("div", { class: "space-y-1" }, [
              el("label", {
                class: "text-xs font-semibold uppercase tracking-wide text-gray-500",
                text: "Trigger keywords (comma-separated)",
              }),
              keywordsInput,
            ]),
            el("div", { class: "space-y-1" }, [
              el("label", { class: "text-xs font-semibold uppercase tracking-wide text-gray-500", text: "Status" }),
              statusSelect,
            ]),
          ]),
          el("div", { class: "space-y-1" }, [
            el("label", {
              class: "text-xs font-semibold uppercase tracking-wide text-gray-500",
              text: "Expected outcomes (markdown)",
            }),
            bodyInput,
          ]),
          el("div", { class: "space-y-2" }, [
            el("label", {
              class: "text-xs font-semibold uppercase tracking-wide text-gray-500",
              text: "Learnings from chat",
            }),
            el("p", {
              class: "text-xs text-gray-500",
              text: "Auto-saved when Figaro notes Gaps. Edit the body above or remove a row here.",
            }),
            learningsHost,
          ]),
          el("div", { class: "flex items-center gap-3" }, [saveButton, saveStatus]),
        ])
      );
    } catch (error) {
      host.replaceChildren(
        el("div", { class: "text-sm text-red-700", text: cleanText(error?.message) || "Could not open playbook" })
      );
    }
  }

  void loadList().catch((error) => {
    host.replaceChildren(
      el("div", { class: "text-sm text-red-700", text: cleanText(error?.message) || "Could not load playbooks" })
    );
  });
}

/* ─── settings ─────────────────────────────────────────────────────── */

const TIER_LABELS = { deep: "Amelia", standard: "Bessie", fast: "Charles" };

function tierLookup(models) {
  const map = new Map();
  for (const [tier, model] of Object.entries(models || {})) {
    if (model) map.set(model, TIER_LABELS[tier] || tier);
  }
  return map;
}

export function renderFigaroSettingsPanels({ el, fetchJson, apiUrl, cleanText, tab }) {
  if (tab === "figaro-config") {
    const host = el("div", { class: "space-y-4" }, [el("div", { class: "text-sm text-gray-500", text: "Loading…" })]);
    (async () => {
      try {
        const cfg = await fetchJson(apiUrl("/api/ai/config"));
        const tiers = tierLookup(cfg.models);
        const rows = (cfg.pricing || []).filter((p) => tiers.has(p.model));
        host.replaceChildren(
          panel(el, "Figaro", [
            el("p", {
              class: "text-sm text-gray-600",
              text: `Service credentials: ${cfg.anthropicKeyPresent ? "configured on the server" : "MISSING — set them in the server secrets file"}`,
            }),
            el("p", {
              class: "text-sm text-gray-600",
              text: `Monthly budget: $${Number(cfg.budgetUsd || 0).toFixed(0)}`,
            }),
            el("div", { class: "overflow-x-auto" }, [
              el("table", { class: "min-w-full text-sm" }, [
                el("thead", {}, [
                  el("tr", { class: "text-left text-gray-500 border-b" }, [
                    el("th", { class: "py-2 pr-4", text: "Capability" }),
                    el("th", { class: "py-2 pr-4", text: "Cost in / MTok" }),
                    el("th", { class: "py-2 pr-4", text: "Cost out / MTok" }),
                  ]),
                ]),
                el(
                  "tbody",
                  {},
                  rows.map((p) =>
                    el("tr", { class: "border-b border-gray-100" }, [
                      el("td", { class: "py-2 pr-4", text: tiers.get(p.model) }),
                      el("td", { class: "py-2 pr-4", text: `$${Number(p.inputPerMtokUsd).toFixed(2)}` }),
                      el("td", { class: "py-2 pr-4", text: `$${Number(p.outputPerMtokUsd).toFixed(2)}` }),
                    ])
                  )
                ),
              ]),
            ]),
            el("div", { class: "flex flex-wrap gap-2 pt-1" }, [
              el("button", {
                type: "button",
                class: "px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50",
                text: "Run connection check",
                onclick: async () => {
                  try {
                    const r = await fetchJson(apiUrl("/api/ai/test-ping"), {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: "{}",
                    });
                    alert(`Connection OK · ${r.usage?.input_tokens || 0} in / ${r.usage?.output_tokens || 0} out · ${r.latencyMs} ms`);
                  } catch (e) {
                    alert(cleanText(e?.message) || "Connection check failed");
                  }
                },
              }),
            ]),
            el("p", {
              class: "text-xs text-gray-500",
              text: "Admin-only for now; owner-scoped access is already wired for later user levels.",
            }),
          ])
        );
      } catch (e) {
        host.replaceChildren(el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
      }
    })();
    return host;
  }

  if (tab === "figaro-usage") {
    const host = el("div", { class: "space-y-4" }, [el("div", { class: "text-sm text-gray-500", text: "Loading…" })]);
    (async () => {
      try {
        const [u, cfg] = await Promise.all([
          fetchJson(apiUrl("/api/ai/usage/summary")),
          fetchJson(apiUrl("/api/ai/config")).catch(() => ({ models: {} })),
        ]);
        const tiers = tierLookup(cfg.models);
        host.replaceChildren(
          panel(el, "Figaro usage", [
            el("p", {
              class: "text-sm font-medium text-gray-900",
              text: `This month: $${Number(u.monthSpendUsd || 0).toFixed(4)} of $${Number(u.budgetUsd || 0).toFixed(0)} (${u.pct || 0}%)`,
            }),
            el("div", { class: "overflow-x-auto" }, [
              el("table", { class: "min-w-full text-sm" }, [
                el("thead", {}, [
                  el("tr", { class: "text-left text-gray-500 border-b" }, [
                    el("th", { class: "py-2 pr-3", text: "When" }),
                    el("th", { class: "py-2 pr-3", text: "Task" }),
                    el("th", { class: "py-2 pr-3", text: "Capability" }),
                    el("th", { class: "py-2 pr-3", text: "Tokens" }),
                    el("th", { class: "py-2 pr-3", text: "Cost" }),
                    el("th", { class: "py-2 pr-3", text: "Status" }),
                  ]),
                ]),
                el(
                  "tbody",
                  {},
                  (u.recent || []).map((c) =>
                    el("tr", { class: "border-b border-gray-100" }, [
                      el("td", {
                        class: "py-2 pr-3 whitespace-nowrap text-xs text-gray-500",
                        text: String(c.createdAt || "").replace("T", " ").slice(0, 19),
                      }),
                      el("td", { class: "py-2 pr-3", text: c.purpose || "—" }),
                      el("td", { class: "py-2 pr-3 text-xs", text: tiers.get(c.model) || "—" }),
                      el("td", { class: "py-2 pr-3", text: `${c.inputTokens || 0}/${c.outputTokens || 0}` }),
                      el("td", { class: "py-2 pr-3", text: `$${Number(c.costUsd || 0).toFixed(4)}` }),
                      el("td", { class: "py-2 pr-3", text: c.status || "—" }),
                    ])
                  )
                ),
              ]),
            ]),
          ])
        );
      } catch (e) {
        host.replaceChildren(el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
      }
    })();
    return host;
  }

  return el("div", { text: "" });
}
